import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Autocomplete
} from '@mui/material';
import {
  AddBox as AddBoxIcon,
  IndeterminateCheckBox as RemoveBoxIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../utils/axiosInstance';
import DocumentInfoAndFiles from './DocumentInfoAndFiles';
import YKienMauPopup from './YKienMauPopup';

export interface ChuyenXuLyPopupProps {
  open: boolean;
  onClose: () => void;
  documentId?: string;
  documentIds?: string[]; // Danh sách ID các văn bản (cho chuyển nhiều văn bản)
  documentData?: any; // Thông tin văn bản
  /** Gọi sau khi chuyển xử lý thành công. documentIdJustProcessed: id văn bản vừa chuyển; suggestedNextDocumentId: id gợi ý xem tiếp (tính trước API). */
  onSuccess?: (payload?: { documentIdJustProcessed?: string | number; suggestedNextDocumentId?: string | number }) => void;
  /** Id văn bản gợi ý xem tiếp (tính từ list trước khi gọi API: văn bản phía sau, hoặc phía trước nếu là bản ghi cuối). */
  suggestedNextDocumentId?: number | string | null;
  action?: string; // Action từ menu API
}

interface UserThamMuu {
  id: number;
  username: string;
  fullname: string;
  position: string;
  position_label?: string; // Label để hiển thị
  department: string;
  email: string;
  roles: string[];
  is_active: boolean;
  created_at: string;
}

interface FileAttachment {
  id: number;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  description: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string | null;
  is_active: boolean;
  preview?: {
    file_path: string;
  };
}

interface AssignUser {
  id: number;
  username: string;
  fullname: string;
  email: string;
  department: string;
  department_id?: number | string;
  position: string;
  position_label?: string; // Label để hiển thị
  roles: string[];
  is_active: boolean;
  fullname_order?: string; // Trường để sắp xếp theo thứ tự
}

interface AssignTreeNode {
  id: string;
  name: string;
  type: 'department' | 'user';
  level: number;
  expanded: boolean;
  children?: AssignTreeNode[];
  user?: AssignUser;
  department?: string;
}


const ChuyenXuLyPopup: React.FC<ChuyenXuLyPopupProps> = ({
  open,
  onClose,
  documentId,
  documentIds,
  documentData,
  onSuccess,
  suggestedNextDocumentId,
  action
}) => { 
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState({
    note: '',
    priority: 'normal',
    deadline: '',
    processingContent: '' // Nội dung xử lý
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noteSuggestions, setNoteSuggestions] = useState<string[]>([]);
  const [noteSuggestionsLoading, setNoteSuggestionsLoading] = useState(false);
  const [users, setUsers] = useState<UserThamMuu[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [selectedMainProcessor, setSelectedMainProcessor] = useState<number | null>(null);
  const [selectedCoProcessors, setSelectedCoProcessors] = useState<(number | string)[]>([]);
  const [isAllCoProcessorsSelected, setIsAllCoProcessorsSelected] = useState(false);
  const [hoveredUserId, setHoveredUserId] = useState<number | string | null>(null);
  const [assignUsers, setAssignUsers] = useState<AssignUser[]>([]);
  const [assignTreeData, setAssignTreeData] = useState<AssignTreeNode[]>([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignData, setAssignData] = useState({
    mainHandler: '',
    coordinators: [] as string[],
    followers: [] as string[]
  });

  // State cho tìm kiếm
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<string[]>([]); // Array of user IDs that match search
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [originalExpandedDepartments, setOriginalExpandedDepartments] = useState<string[]>([]); // Lưu trạng thái expand ban đầu
  const [highlightedUsers, setHighlightedUsers] = useState<string[]>([]); // Users được highlight
  const [showOnlySelected, setShowOnlySelected] = useState(false); // Chỉ hiển thị những người đã chọn

  // State cho dialog chọn vai trò phòng ban
  const [departmentRoleDialogOpen, setDepartmentRoleDialogOpen] = useState(false);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('');
  const [selectedDepartmentName, setSelectedDepartmentName] = useState<string>('');
  const [isAllDepartmentsSelected, setIsAllDepartmentsSelected] = useState(false);
  const [isAllDepartmentsCoordSelected, setIsAllDepartmentsCoordSelected] = useState(false);
  const [isAllUsersSelected, setIsAllUsersSelected] = useState(false);
  const [isAllCoordinatorsSelected, setIsAllCoordinatorsSelected] = useState(false);
  const [showFiles, setShowFiles] = useState(true);
  const [yKienMauOpen, setYKienMauOpen] = useState(false);
  
  // State cho quick selection
  const [quickSelection, setQuickSelection] = useState<string>('');
  const [personalGroups, setPersonalGroups] = useState<any[]>([]);
  const [selectedPersonalGroup, setSelectedPersonalGroup] = useState<string>('');
  const [personalGroupsLoading, setPersonalGroupsLoading] = useState(false);
  
  // State cho thông tin user hiện tại
  const [currentUserInfo, setCurrentUserInfo] = useState<{
    roles: string[];
    department: string;
    department_id?: number | string;
    department_id_phutrach?: (number | string)[];
    canViewAllUsers: boolean;
  } | null>(null);

  // Helper function để lấy thông tin current user
  const getCurrentUser = () => {
    const currentUserStr = localStorage.getItem('currentUser');
    return currentUserStr ? JSON.parse(currentUserStr) : null;
  };

  // Helper function để lấy current user ID
  const getCurrentUserId = () => {
    const currentUser = getCurrentUser();
    return currentUser?.id;
  };

  // Helper function để kiểm tra tất cả user của phòng ban có được chọn làm coordinator không
  const isDepartmentAllCoordinatorsSelected = (departmentId: string) => {
    const department = assignTreeData.find(node => node.id === departmentId);
    if (!department || !department.children) return false;

    const currentUserId = getCurrentUserId();
    const mainHandlerId = assignData.mainHandler;
    
    // Lọc bỏ current user và main handler
    const eligibleUsers = department.children.filter(child => 
      child.user?.id !== currentUserId && child.id !== mainHandlerId
    );

    if (eligibleUsers.length === 0) return false;

    // Kiểm tra xem tất cả user hợp lệ có được chọn không
    return eligibleUsers.every(user => assignData.coordinators.includes(user.id));
  };

  // Helper function để kiểm tra trạng thái indeterminate cho phòng ban coordinator
  const isDepartmentIndeterminateCoordinators = (departmentId: string) => {
    const department = assignTreeData.find(node => node.id === departmentId);
    if (!department || !department.children) return false;

    const currentUserId = getCurrentUserId();
    const mainHandlerId = assignData.mainHandler;
    
    // Lọc bỏ current user và main handler
    const eligibleUsers = department.children.filter(child => 
      child.user?.id !== currentUserId && child.id !== mainHandlerId
    );

    if (eligibleUsers.length === 0) return false;

    const selectedCount = eligibleUsers.filter(user => assignData.coordinators.includes(user.id)).length;
    return selectedCount > 0 && selectedCount < eligibleUsers.length;
  };

  // Helper function để xử lý check all cho coordinator của phòng ban
  const handleDepartmentAllCoordinatorsChange = (departmentId: string) => {
    const department = assignTreeData.find(node => node.id === departmentId);
    if (!department || !department.children) return;

    const currentUserId = getCurrentUserId();
    const mainHandlerId = assignData.mainHandler;
    
    // Lọc bỏ current user và main handler
    const eligibleUsers = department.children.filter(child => 
      child.user?.id !== currentUserId && child.id !== mainHandlerId
    );

    if (eligibleUsers.length === 0) return;

    const isAllSelected = isDepartmentAllCoordinatorsSelected(departmentId);
    
    if (isAllSelected) {
      // Bỏ chọn tất cả user của phòng ban
      const userIdsToRemove = eligibleUsers.map(user => user.id);
      setAssignData(prev => ({
        ...prev,
        coordinators: prev.coordinators.filter(id => !userIdsToRemove.includes(id))
      }));
      
      // Cập nhật selectedCoProcessors - xóa users khỏi danh sách
      setSelectedCoProcessors(prev => {
        const userIdsToRemoveNumbers = eligibleUsers
          .filter(user => user.user?.id)
          .map(user => user.user!.id);
        return prev.filter(id => !userIdsToRemoveNumbers.includes(id as number));
      });
    } else {
      // Chọn tất cả user của phòng ban
      const userIdsToAdd = eligibleUsers.map(user => user.id);
      setAssignData(prev => ({
        ...prev,
        coordinators: Array.from(new Set(prev.coordinators.concat(userIdsToAdd)))
      }));
      
      // Cập nhật selectedCoProcessors - thêm users vào danh sách
      setSelectedCoProcessors(prev => {
        const newList = [...prev];
        eligibleUsers.forEach(user => {
          if (user.user?.id && !newList.includes(user.user.id)) {
            newList.push(user.user.id);
          }
        });
        return newList;
      });
    }
  };

  // Helper function để kiểm tra tất cả user của phòng ban có được chọn làm follower không
  const isDepartmentAllFollowersSelected = (departmentId: string) => {
    const department = assignTreeData.find(node => node.id === departmentId);
    if (!department || !department.children) return false;

    const currentUserId = getCurrentUserId();
    const mainHandlerId = assignData.mainHandler;
    
    // Lọc bỏ current user và main handler
    const eligibleUsers = department.children.filter(child => 
      child.user?.id !== currentUserId && child.id !== mainHandlerId
    );

    if (eligibleUsers.length === 0) return false;

    // Kiểm tra xem tất cả user hợp lệ có được chọn không
    return eligibleUsers.every(user => assignData.followers.includes(user.id));
  };

  // Helper function để kiểm tra trạng thái indeterminate cho phòng ban follower
  const isDepartmentIndeterminateFollowers = (departmentId: string) => {
    const department = assignTreeData.find(node => node.id === departmentId);
    if (!department || !department.children) return false;

    const currentUserId = getCurrentUserId();
    const mainHandlerId = assignData.mainHandler;
    
    // Lọc bỏ current user và main handler
    const eligibleUsers = department.children.filter(child => 
      child.user?.id !== currentUserId && child.id !== mainHandlerId
    );

    if (eligibleUsers.length === 0) return false;

    const selectedCount = eligibleUsers.filter(user => assignData.followers.includes(user.id)).length;
    return selectedCount > 0 && selectedCount < eligibleUsers.length;
  };

  // Helper function để xử lý check all cho follower của phòng ban
  const handleDepartmentAllFollowersChange = (departmentId: string) => {
    const department = assignTreeData.find(node => node.id === departmentId);
    if (!department || !department.children) return;

    const currentUserId = getCurrentUserId();
    const mainHandlerId = assignData.mainHandler;
    
    // Lọc bỏ current user và main handler
    const eligibleUsers = department.children.filter(child => 
      child.user?.id !== currentUserId && child.id !== mainHandlerId
    );

    if (eligibleUsers.length === 0) return;

    const isAllSelected = isDepartmentAllFollowersSelected(departmentId);
    
    if (isAllSelected) {
      // Bỏ chọn tất cả user của phòng ban
      const userIdsToRemove = eligibleUsers.map(user => user.id);
      setAssignData(prev => ({
        ...prev,
        followers: prev.followers.filter(id => !userIdsToRemove.includes(id))
      }));
      
      // Cập nhật selectedCoProcessors - xóa users khỏi danh sách
      setSelectedCoProcessors(prev => {
        const userIdsToRemoveNumbers = eligibleUsers
          .filter(user => user.user?.id)
          .map(user => user.user!.id);
        return prev.filter(id => !userIdsToRemoveNumbers.includes(id as number));
      });
    } else {
      // Chọn tất cả user của phòng ban
      const userIdsToAdd = eligibleUsers.map(user => user.id);
      setAssignData(prev => ({
        ...prev,
        followers: Array.from(new Set(prev.followers.concat(userIdsToAdd)))
      }));
      
      // Cập nhật selectedCoProcessors - thêm users vào danh sách
      setSelectedCoProcessors(prev => {
        const newList = [...prev];
        eligibleUsers.forEach(user => {
          if (user.user?.id && !newList.includes(user.user.id)) {
            newList.push(user.user.id);
          }
        });
        return newList;
      });
    }
  };

  // Helper function để chuyển đổi department ID thành trưởng phòng ID
  const getDepartmentHeadId = (nodeId: string) => {
    if (nodeId.startsWith('dept_')) {
      const departmentName = nodeId.replace('dept_', '');
      const departmentNode = assignTreeData.find(node => node.name === departmentName);
      
      if (departmentNode && departmentNode.children) {
        // Tìm trưởng phòng
        const departmentHead = departmentNode.children.find(child => {
          const position = child.user?.position?.toLowerCase() || '';
          return position.includes('trưởng phòng') && !position.includes('phó');
        });
        
        if (departmentHead) {
          return departmentHead.id;
        } else {
          // Nếu không tìm thấy trưởng phòng, trả về null để không gửi dữ liệu
          return null;
        }
      }
    }
    return nodeId; // Trả về ID gốc nếu không phải department
  };

  // Auto-dismiss error message sau 5 giây
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000); // 5 giây

      return () => clearTimeout(timer);
    }
  }, [error]);

  // Function để loại bỏ dấu tiếng Việt
  const removeDiacritics = (str: string) => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D')
      .toLowerCase();
  };

  // Function để xử lý tìm kiếm
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      handleClearSearch();
      return;
    }

    setSearchLoading(true);

    // Lưu trạng thái expand hiện tại
    const currentExpanded = assignTreeData
      .filter(node => node.expanded)
      .map(node => node.id);
    setOriginalExpandedDepartments(currentExpanded);

    // Tạo độ trễ để hiển thị loading (UX)
    await new Promise(resolve => setTimeout(resolve, 300));

    const searchNormalized = removeDiacritics(searchTerm.trim());
    const matchingUserIds: string[] = [];
    const departmentsToExpand: string[] = [];

    // Tìm kiếm trong assignTreeData
    assignTreeData.forEach(department => {
      if (department.children) {
        department.children.forEach(user => {
          if (user.user) {
            const fullname = removeDiacritics(user.user.fullname || '');
            const username = removeDiacritics(user.user.username || '');
            const email = removeDiacritics(user.user.email || '');
            const position = removeDiacritics(user.user.position || '');
            const departmentName = removeDiacritics(user.user.department || '');

            if (
              fullname.includes(searchNormalized) ||
              username.includes(searchNormalized) ||
              email.includes(searchNormalized) ||
              position.includes(searchNormalized) ||
              departmentName.includes(searchNormalized)
            ) {
              matchingUserIds.push(user.id);
              if (!departmentsToExpand.includes(department.id)) {
                departmentsToExpand.push(department.id);
              }
            }
          }
        });
      }
    });

    setSearchResults(matchingUserIds);
    setHighlightedUsers(matchingUserIds);

    // Expand các phòng ban có kết quả tìm kiếm
    setAssignTreeData(prev => {
      return prev.map(dept => ({
        ...dept,
        expanded: departmentsToExpand.includes(dept.id)
      }));
    });

    setSearchLoading(false);

    // Tự động cuộn đến kết quả đầu tiên sau khi render
    setTimeout(() => {
      if (matchingUserIds.length > 0) {
        const firstUserId = matchingUserIds[0];
        const element = document.getElementById(`user-row-${firstUserId}`);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }
    }, 100);
  };

  // Function để clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setHighlightedUsers([]);
    setSearchLoading(false);
    
    // Khôi phục trạng thái expand ban đầu
    setAssignTreeData(prev => {
      return prev.map(dept => ({
        ...dept,
        expanded: originalExpandedDepartments.includes(dept.id)
      }));
    });
    
    // Reset original expanded departments
    setOriginalExpandedDepartments([]);
  };

  // Effect để clear search results khi search term trở thành rỗng
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setHighlightedUsers([]);
    }
  }, [searchTerm]);

  // Effect để tự động xóa highlight sau 3 giây
  useEffect(() => {
    if (highlightedUsers.length > 0) {
      const timer = setTimeout(() => {
        setHighlightedUsers([]);
      }, 3000); // 3 giây

      return () => clearTimeout(timer);
    }
  }, [highlightedUsers]);

  // Function để xử lý khi chọn phòng ban
  const handleDepartmentSelect = (nodeId: string, nodeName: string) => {
    setSelectedDepartmentId(nodeId);
    setSelectedDepartmentName(nodeName);
    setDepartmentRoleDialogOpen(true);
  };

  // Function để xử lý chọn vai trò cho phòng ban
  const handleDepartmentRoleSelect = (role: 'coord' | 'follow') => {
    setAssignData(prev => {
      const newData = { ...prev };
      
      if (role === 'coord') {
        // Chọn phòng ban làm phối hợp
        if (!newData.coordinators.includes(selectedDepartmentId)) {
          newData.coordinators.push(selectedDepartmentId);
        }
        // Xóa khỏi vai trò khác
        newData.followers = newData.followers.filter(id => id !== selectedDepartmentId);
      } else if (role === 'follow') {
        // Chọn phòng ban làm xem để biết
        if (!newData.followers.includes(selectedDepartmentId)) {
          newData.followers.push(selectedDepartmentId);
        }
        // Xóa khỏi vai trò khác
        newData.coordinators = newData.coordinators.filter(id => id !== selectedDepartmentId);
        
        // Cập nhật selectedCoProcessors - thêm phòng ban vào danh sách
        const departmentId = `dept_${selectedDepartmentId}`;
        setSelectedCoProcessors(prev => {
          if (!prev.includes(departmentId)) {
            return [...prev, departmentId];
          }
          return prev;
        });
      }
      
      return newData;
    });
    
    setDepartmentRoleDialogOpen(false);
  };

  // Load danh sách user tham mưu khi popup mở
  useEffect(() => {
    if (open) {
      fetchAssignUsers();
      fetchPersonalGroups(); // Load danh sách nhóm cá nhân mặc định
      
      // Load thông tin user hiện tại
      const currentUser = getCurrentUser();
      if (currentUser) {
        const userRoles = currentUser?.roles || [];
        const allowedRoles = ['VAN_THU_DEN', 'VAN_THU_DI', 'LANH_DAO_BAN'];
        const canViewAllUsers = userRoles.some((role: string) => allowedRoles.includes(role));

        const departmentId = currentUser?.department_id;
        const departmentIdPhuTrach = currentUser?.department_id_phutrach || [];

        setCurrentUserInfo({
          roles: userRoles,
          department: currentUser?.department || '',
          department_id: departmentId,
          department_id_phutrach: departmentIdPhuTrach,
          canViewAllUsers
        });
      }
    }
  }, [open]);

  // Cập nhật trạng thái "All" khi có thay đổi
  useEffect(() => {
    const availableUserIds = selectedMainProcessor 
      ? users.map(user => user.id).filter(id => id !== selectedMainProcessor)
      : users.map(user => user.id);
    
    const allSelected = availableUserIds.length > 0 && 
      availableUserIds.every(id => selectedCoProcessors.includes(id));
    
    setIsAllCoProcessorsSelected(allSelected);
  }, [selectedCoProcessors, selectedMainProcessor, users]);


  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  // Hàm lấy danh sách note gần đây
  const fetchNoteSuggestions = async () => {
    try {
      setNoteSuggestionsLoading(true);
      const response = await axiosInstance.get('/xlvb/incoming-documents/danhsach-note-top10', {
        params: {
          type: 'incoming_documents.chuyen_xuly'
        }
      });
      
      if (response.data.code === 0 && response.data.data?.notes) {
        // Lấy danh sách note từ response
        const notes = response.data.data.notes.map((item: any) => item.note);
        setNoteSuggestions(notes);
      } else {
        setNoteSuggestions([]);
      }
    } catch (error) {

      setNoteSuggestions([]);
    } finally {
      setNoteSuggestionsLoading(false);
    }
  };

  // Gọi API khi mở popup hoặc khi focus vào ô nhập
  useEffect(() => {
    if (open) {
      fetchNoteSuggestions();
    }
  }, [open]);

  const handleMainProcessorChange = (userId: number) => {
    setSelectedMainProcessor(userId);
    // Nếu user này đã được chọn làm đồng xử lý, bỏ chọn
    setSelectedCoProcessors(prev => prev.filter(id => id !== userId));
    setIsAllCoProcessorsSelected(false);
  };

  const handleCoProcessorChange = (userId: number) => {
    setSelectedCoProcessors(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Hàm xử lý thay đổi vai trò từ listbox trong "Danh sách người dùng đã chọn"
  const handleRoleChange = (itemId: number | string, newRole: 'main' | 'coord' | 'follow') => {
    if (typeof itemId === 'string' && itemId.startsWith('dept_')) {
      // Xử lý department
      const departmentNodeId = itemId.replace('dept_', '');
      const oldMainHandler = assignData.mainHandler;
      
      setAssignData(prev => {
        const newData = { ...prev };
        
        // Xóa khỏi tất cả các vai trò cũ
        newData.mainHandler = newData.mainHandler === departmentNodeId ? '' : newData.mainHandler;
        newData.coordinators = newData.coordinators.filter(id => id !== departmentNodeId);
        newData.followers = newData.followers.filter(id => id !== departmentNodeId);
        
        // Thêm vào vai trò mới
        if (newRole === 'main') {
          newData.mainHandler = departmentNodeId;
        } else if (newRole === 'coord') {
          newData.coordinators.push(departmentNodeId);
        } else if (newRole === 'follow') {
          newData.followers.push(departmentNodeId);
        }
        
        return newData;
      });
      
      // Cập nhật selectedCoProcessors
      setSelectedCoProcessors(prev => {
        if (newRole === 'main') {
          // Xóa main handler cũ nếu có
          const filteredList = prev.filter(id => {
            if (oldMainHandler && oldMainHandler.startsWith('dept_')) {
              const oldDeptId = oldMainHandler.replace('dept_', '');
              if (id === `dept_${oldDeptId}`) {
                return false;
              }
            }
            return true;
          });
          // Thêm main handler mới
          if (!filteredList.includes(itemId)) {
            return [...filteredList, itemId];
          }
          return filteredList;
        } else {
          // Đảm bảo department được thêm vào selectedCoProcessors cho coord/follow
          if (!prev.includes(itemId)) {
            return [...prev, itemId];
          }
          return prev;
        }
      });
    } else {
      // Xử lý user
      const userNode = assignTreeData.flatMap(node => 
        node.children?.filter(child => child.type === 'user') || []
      ).find(u => u.user?.id === itemId);
      
      if (userNode) {
        const userNodeId = userNode.id;
        const oldMainHandler = assignData.mainHandler;
        
        setAssignData(prev => {
          const newData = { ...prev };
          
          // Xóa khỏi tất cả các vai trò cũ
          newData.mainHandler = newData.mainHandler === userNodeId ? '' : newData.mainHandler;
          newData.coordinators = newData.coordinators.filter(id => id !== userNodeId);
          newData.followers = newData.followers.filter(id => id !== userNodeId);
          
          // Thêm vào vai trò mới
          if (newRole === 'main') {
            newData.mainHandler = userNodeId;
          } else if (newRole === 'coord') {
            newData.coordinators.push(userNodeId);
          } else if (newRole === 'follow') {
            newData.followers.push(userNodeId);
          }
          
          return newData;
        });
        
        // Cập nhật selectedCoProcessors
        setSelectedCoProcessors(prev => {
          if (newRole === 'main') {
            // Xóa main handler cũ nếu có
            const filteredList = prev.filter(id => {
              if (oldMainHandler && oldMainHandler.startsWith('user_')) {
                const oldUserId = oldMainHandler.replace('user_', '');
                if (id === parseInt(oldUserId)) {
                  return false;
                }
              }
              return true;
            });
            // Thêm main handler mới
            if (!filteredList.includes(itemId)) {
              return [...filteredList, itemId];
            }
            return filteredList;
          } else {
            // Đảm bảo user được thêm vào selectedCoProcessors cho coord/follow
            if (!prev.includes(itemId)) {
              return [...prev, itemId];
            }
            return prev;
          }
        });
      }
    }
  };

  const handleRemoveUser = (itemId: number | string) => {
    // Xóa khỏi selectedCoProcessors
    setSelectedCoProcessors(prev => prev.filter(id => id !== itemId));
    
    if (typeof itemId === 'string' && itemId.startsWith('dept_')) {
      // Xử lý department
      const departmentNodeId = itemId.replace('dept_', '');
      
      // Xóa khỏi tất cả các vai trò trong assignData
      setAssignData(prev => ({
        ...prev,
        // Xóa khỏi xử lý chính (radio)
        mainHandler: prev.mainHandler === departmentNodeId ? '' : prev.mainHandler,
        // Xóa khỏi phối hợp (checkbox)
        coordinators: prev.coordinators.filter(id => id !== departmentNodeId),
        // Xóa khỏi xem để biết (checkbox)
        followers: prev.followers.filter(id => id !== departmentNodeId)
      }));
    } else {
      // Xử lý user
      const userNode = assignTreeData.flatMap(node => 
        node.children?.filter(child => child.type === 'user') || []
      ).find(u => u.user?.id === itemId);
      
      if (userNode) {
        const userNodeId = userNode.id;
        
        // Xóa khỏi tất cả các vai trò trong assignData
        setAssignData(prev => ({
          ...prev,
          // Xóa khỏi xử lý chính (radio)
          mainHandler: prev.mainHandler === userNodeId ? '' : prev.mainHandler,
          // Xóa khỏi phối hợp (checkbox)
          coordinators: prev.coordinators.filter(id => id !== userNodeId),
          // Xóa khỏi xem để biết (checkbox)
          followers: prev.followers.filter(id => id !== userNodeId)
        }));
      }
    }
  };

  const handleAllCoProcessorsChange = () => {
    if (isAllCoProcessorsSelected) {
      setSelectedCoProcessors([]);
    } else {
      // Chọn tất cả user trừ user đã chọn làm xử lý chính
      const allUserIds = users.map(user => user.id);
      const availableUserIds = selectedMainProcessor 
        ? allUserIds.filter(id => id !== selectedMainProcessor)
        : allUserIds;
      setSelectedCoProcessors(availableUserIds);
    }
    setIsAllCoProcessorsSelected(!isAllCoProcessorsSelected);
  };


  // Fetch assign users
  const fetchAssignUsers = async () => {
    setAssignLoading(true);
    try {
      const response = await axiosInstance.get('/users', {
        params: {
          page_size: -1 // Lấy tất cả users
        }
      });

      let usersList: AssignUser[];
      if (response.data?.data?.records && Array.isArray(response.data.data.records)) {
        usersList = response.data.data.records;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        usersList = response.data.data;
      } else {
        throw new Error('Invalid response format');
      }

      setAssignUsers(usersList);
    } catch (error) {

    } finally {
      setAssignLoading(false);
    }
  };

  // Build tree data từ assign users
  const buildAssignTreeData = (userList: AssignUser[]): AssignTreeNode[] => {
    const result: AssignTreeNode[] = [];
    
    // Lấy thông tin user hiện tại để kiểm tra role
    const currentUser = getCurrentUser();
    const userRoles = currentUser?.roles || [];
    const userDepartment = currentUser?.department;
    const userDepartmentId = currentUser?.department_id;
    const userResponsibleDepartments: (number | string)[] = currentUser?.department_id_phutrach || [];
    
    // Kiểm tra các role được phép xem tất cả danh sách
    const allowedRoles = ['VAN_THU_DEN', 'VAN_THU_DI', 'LANH_DAO_BAN'];
    const canViewAllUsers = userRoles.some((role: string) => allowedRoles.includes(role));
    
    // Lọc bỏ users:
    // - Không có department hoặc department là "Chưa cập nhật"
    // - Thuộc phân hệ tư vấn nhà thầu (tuvan_nhathau = 1)
    let filteredUsers = userList.filter(user => {
      const rawUser: any = user as any;
      const isTuVanNhaThau =
        rawUser?.tuvan_nhathau === 1 ||
        rawUser?.tuvan_nhathau === '1' ||
        rawUser?.tuvan_nhathau === true;

      return (
        !!user.department &&
        user.department !== 'Chưa cập nhật' &&
        !isTuVanNhaThau
      );
    });
    
    // Nếu user không có quyền xem tất cả, chỉ hiển thị phòng ban của họ
    // và (nếu có) các phòng ban mà họ phụ trách (department_id_phutrach)
    if (!canViewAllUsers) {
      const allowedDepartmentIds: (number | string)[] = [];

      if (userDepartmentId) {
        allowedDepartmentIds.push(String(userDepartmentId));
      }

      if (Array.isArray(userResponsibleDepartments) && userResponsibleDepartments.length > 0) {
        userResponsibleDepartments.forEach((deptId) => {
          const normalized = String(deptId);
          if (!allowedDepartmentIds.includes(normalized)) {
            allowedDepartmentIds.push(normalized);
          }
        });
      }

      // Nếu không xác định được department_id nào thì fallback về department name cũ
      if (allowedDepartmentIds.length === 0 && userDepartment) {
        filteredUsers = filteredUsers.filter(user =>
          user.department === userDepartment
        );
      } else if (allowedDepartmentIds.length > 0) {
        filteredUsers = filteredUsers.filter((user) => {
          const rawUser: any = user as any;
          const deptId = rawUser?.department_id;
          if (deptId === null || deptId === undefined) return false;
          return allowedDepartmentIds.includes(String(deptId));
        });
      }
    }
    
    // Tạo map users theo department
    const userMap = new Map<string, AssignUser[]>();
    filteredUsers.forEach(user => {
      if (user.department) {
        const deptUsers = userMap.get(user.department) || [];
        deptUsers.push(user);
        userMap.set(user.department, deptUsers);
      }
    });

    // Tạo tree từ departments
    userMap.forEach((deptUsers, deptName) => {
      // Sắp xếp users trong department: trưởng phòng -> phó trưởng phòng -> nhân viên
      const sortedUsers = deptUsers.sort((a, b) => {
        // Hàm tính điểm ưu tiên
        const getPriority = (user: AssignUser) => {
          const position = (user.position || '').toLowerCase();
          const roles = user.roles || [];
          
          // Xử lý đặc biệt cho department "Lãnh đạo ban"
          if (deptName === 'Lãnh đạo ban') {
            // Trưởng ban: điểm cao nhất
            if (position.includes('trưởng ban') && !position.includes('phó')) {
              return 4;
            }
            // Phó trưởng ban: điểm cao
            if (position.includes('phó trưởng ban') || position.includes('pho truong ban')) {
              return 3;
            }
            // Các lãnh đạo khác trong ban
            if (roles.includes('LANH_DAO_BAN')) {
              return 2;
            }
            // Nhân viên: điểm thấp nhất
            return 1;
          }
          
          // Xử lý cho các department khác
          // Trưởng phòng: điểm cao nhất (ưu tiên position trước)
          if (position.includes('trưởng phòng') && !position.includes('phó')) {
            return 3;
          }
          // Phó trưởng phòng: điểm trung bình
          if (position.includes('phó trưởng phòng') || position.includes('pho truong phong')) {
            return 2;
          }
          // Các role TRUONG_PHONG khác (có thể là phó trưởng phòng): điểm trung bình
          if (roles.includes('TRUONG_PHONG')) {
            return 2;
          }
          // Nhân viên: điểm thấp nhất
          return 1;
        };
        
        const priorityA = getPriority(a);
        const priorityB = getPriority(b);
        
        // Sắp xếp giảm dần theo điểm ưu tiên
        if (priorityA !== priorityB) {
          return priorityB - priorityA;
        }
        
        // Nếu cùng điểm ưu tiên thì sắp xếp theo fullname_order (tăng dần), sau đó theo tên
        if (a.fullname_order && b.fullname_order) {
          return a.fullname_order.localeCompare(b.fullname_order);
        }
        // Nếu một trong hai không có fullname_order, ưu tiên người có fullname_order
        if (a.fullname_order && !b.fullname_order) {
          return -1;
        }
        if (!a.fullname_order && b.fullname_order) {
          return 1;
        }
        // Nếu cả hai đều không có fullname_order, sắp xếp theo tên
        return a.fullname.localeCompare(b.fullname);
      });

      const departmentNode: AssignTreeNode = {
        id: `dept_${deptName}`,
        name: deptName,
        type: 'department',
        level: 0,
        expanded: false, // Mặc định đóng
        children: [],
        department: deptName
      };

      // Thêm users đã sắp xếp của department này
      sortedUsers.forEach(user => {
        departmentNode.children!.push({
          id: `user_${user.id}`,
          name: user.fullname,
          type: 'user',
          level: 1,
          expanded: false,
          user: user
        });
      });

      result.push(departmentNode);
    });

    // Sắp xếp departments theo thứ tự cụ thể
    const departmentOrder: { [key: string]: number } = {
      // 1. Ban Lãnh đạo
      'Lãnh đạo ban': 1,
      'Ban Lãnh đạo': 1,
      // 2. Phòng HCTC
      'Phòng Hành chính Tổ chức': 2,
      'Phòng HCTC': 2,
      'HCTC': 2,
      // 3. Phòng Kế hoạch tổng hợp
      'Phòng Kế hoạch Tổng hợp': 3,
      'Phòng Kế hoạch - Tổng hợp': 3,
      'Kế hoạch Tổng hợp': 3,
      'Kế hoạch - Tổng hợp': 3,
      // 4. Phòng Kỹ thuật thẩm định
      'Phòng Kỹ thuật Thẩm định': 4,
      'Phòng Kỹ thuật thẩm định': 4,
      'Kỹ thuật Thẩm định': 4,
      'Kỹ thuật thẩm định': 4,
      // 5. Phòng Tài chính kế toán
      'Phòng Tài chính Kế toán': 5,
      'Phòng Tài chính KT': 5,
      'Tài chính Kế toán': 5,
      'Tài chính KT': 5,
      // 6. Phòng DA1
      'Quản lý thực hiện dự án 1': 6,
      'Phòng QLDA1': 6,
      'Phòng DA1': 6,
      'DA1': 6,
      // 7. Phòng DA2
      'Quản lý thực hiện dự án 2': 7,
      'Phòng QLDA2': 7,
      'Phòng DA2': 7,
      'DA2': 7,
      // 8. Phòng DA3
      'Quản lý thực hiện dự án 3': 8,
      'Phòng QLDA3': 8,
      'Phòng DA3': 8,
      'DA3': 8,
      // 9. Phòng DA4
      'Quản lý thực hiện dự án 4': 9,
      'Phòng QLDA4': 9,
      'Phòng DA4': 9,
      'DA4': 9,
      // 10. IT
      'IT': 10,
      'Phòng IT': 10
    };

    result.sort((a, b) => {
      const getOrder = (deptName: string): number => {
        // Normalize tên phòng ban (loại bỏ dấu, khoảng trắng thừa, chuyển về lowercase)
        const normalizedName = removeDiacritics(deptName.trim().toLowerCase());
        
        // Tìm exact match trước (case-insensitive, không dấu)
        for (const [key, order] of Object.entries(departmentOrder)) {
          const normalizedKey = removeDiacritics(key.toLowerCase());
          if (normalizedName === normalizedKey) {
            return order;
          }
        }
        
        // Tìm partial match (chứa từ khóa) - ưu tiên match dài hơn
        const sortedKeys = Object.keys(departmentOrder).sort((a, b) => b.length - a.length);
        for (const key of sortedKeys) {
          const normalizedKey = removeDiacritics(key.toLowerCase());
          
          // Kiểm tra nếu tên phòng ban chứa key hoặc key chứa tên phòng ban
          if (normalizedName.includes(normalizedKey) || normalizedKey.includes(normalizedName)) {
            return departmentOrder[key];
          }
        }
        
        // Không tìm thấy -> đặt vào cuối (11)
        return 11;
      };

      const orderA = getOrder(a.name);
      const orderB = getOrder(b.name);

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      // Nếu cùng thứ tự, sắp xếp theo tên
      return a.name.localeCompare(b.name);
    });

    return result;
  };

  // Cập nhật tree data khi có assign users
  useEffect(() => {
    if (assignUsers.length > 0) {
      const tree = buildAssignTreeData(assignUsers);
      setAssignTreeData(tree);
      
      // Tự động expand nếu chỉ có 1 phòng ban
      if (tree.length === 1) {
        const updatedTree = tree.map(node => ({
          ...node,
          expanded: true
        }));
        setAssignTreeData(updatedTree);
      }
    }
  }, [assignUsers]);

  // Cập nhật trạng thái "All" cho departments
  useEffect(() => {
    const departmentIds = assignTreeData.map(node => node.id);
    const allDepartmentsSelected = departmentIds.length > 0 && 
      departmentIds.every(id => assignData.followers.includes(id));
    setIsAllDepartmentsSelected(allDepartmentsSelected);
  }, [assignData.followers, assignTreeData]);

  // Cập nhật trạng thái "All" cho users
  useEffect(() => {
    const currentUserId = getCurrentUserId();
    
    const userIds = assignTreeData.flatMap(node => 
      node.children ? node.children.map(child => child.id) : []
    );
    
    // Loại trừ current user khỏi danh sách
    const filteredUserIds = userIds.filter(id => {
      const userId = id.replace('user_', '');
      return userId !== currentUserId?.toString();
    });
    
    // Tính toán users cần được chọn (loại trừ user xử lý chính nếu có)
    const expectedUserIds = assignData.mainHandler ? 
      filteredUserIds.filter(id => id !== assignData.mainHandler) : 
      filteredUserIds;
    
    const allUsersSelected = expectedUserIds.length > 0 && 
      expectedUserIds.every(id => assignData.followers.includes(id));
    setIsAllUsersSelected(allUsersSelected);
  }, [assignData.followers, assignData.mainHandler, assignTreeData]);

  // Cập nhật trạng thái "All" cho departments trong coordinators
  useEffect(() => {
    const departmentIds = assignTreeData.map(node => node.id);
    const allDepartmentsCoordSelected = departmentIds.length > 0 && 
      departmentIds.every(id => assignData.coordinators.includes(id));
    setIsAllDepartmentsCoordSelected(allDepartmentsCoordSelected);
  }, [assignData.coordinators, assignTreeData]);

  // Cập nhật trạng thái "All" cho coordinators (chỉ cá nhân, không bao gồm phòng ban)
  useEffect(() => {
    const currentUserId = getCurrentUserId();
    
    const userIds = assignTreeData.flatMap(node => 
      node.children ? node.children.map(child => child.id) : []
    );
    
    // Loại trừ current user khỏi danh sách
    const filteredUserIds = userIds.filter(id => {
      const userId = id.replace('user_', '');
      return userId !== currentUserId?.toString();
    });
    
    // Tính toán users cần được chọn (loại trừ user xử lý chính nếu có)
    const expectedUserIds = assignData.mainHandler ? 
      filteredUserIds.filter(id => id !== assignData.mainHandler) : 
      filteredUserIds;
    
    const allCoordinatorsSelected = expectedUserIds.length > 0 && 
      expectedUserIds.every(id => assignData.coordinators.includes(id));
    setIsAllCoordinatorsSelected(allCoordinatorsSelected);
  }, [assignData.coordinators, assignData.mainHandler, assignTreeData]);

  // Tự động xóa các item không có vai trò khỏi selectedCoProcessors
  useEffect(() => {
    setSelectedCoProcessors(prev => {
      return prev.filter(itemId => {
        if (typeof itemId === 'string' && itemId.startsWith('dept_')) {
          // Xử lý department
          const departmentNodeId = itemId.replace('dept_', '');
          const isMainHandler = assignData.mainHandler === departmentNodeId;
          const isCoordinator = assignData.coordinators.includes(departmentNodeId);
          const isFollower = assignData.followers.includes(departmentNodeId);
          return isMainHandler || isCoordinator || isFollower;
        } else {
          // Xử lý user
          const allUsers = assignTreeData.flatMap(node => 
            node.children?.filter(child => child.type === 'user') || []
          );
          const user = allUsers.find(u => u.user?.id === itemId);
          if (!user) return false;
          
          const userNodeId = user.id;
          const isMainHandler = assignData.mainHandler === userNodeId;
          const isCoordinator = assignData.coordinators.includes(userNodeId);
          const isFollower = assignData.followers.includes(userNodeId);
          return isMainHandler || isCoordinator || isFollower;
        }
      });
    });
  }, [assignData, assignTreeData]);

  // Toggle expand/collapse node
  const toggleAssignNode = (nodeId: string) => {
    setAssignTreeData(prev => {
      const updateNode = (nodes: AssignTreeNode[]): AssignTreeNode[] => {
        return nodes.map(node => {
          if (node.id === nodeId) {
            return { ...node, expanded: !node.expanded };
          }
          if (node.children) {
            return { ...node, children: updateNode(node.children) };
          }
          return node;
        });
      };
      return updateNode(prev);
    });
  };

  // Hàm xử lý khi chọn một loại trong assign table
  const handleAssignSelectType = (nodeId: string, type: 'main' | 'coord' | 'follow') => {
    const node = assignTreeData.find(n => n.id === nodeId) || 
                 assignTreeData.flatMap(n => n.children || []).find(n => n.id === nodeId);
    
    setAssignData(prev => {
      const newData = { ...prev };
      
      if (type === 'follow') {
        // Toggle xem để biết
        let isCurrentlySelected = newData.followers.includes(nodeId);
        
        if (node?.type === 'department') {
          // Xử lý checkbox phòng ban - chọn trực tiếp cho "Xem để biết"
          if (isCurrentlySelected) {
            // Bỏ chọn phòng ban
            newData.followers = newData.followers.filter(id => id !== nodeId);
            
            // Cập nhật selectedCoProcessors - xóa phòng ban khỏi danh sách
            const departmentId = `dept_${nodeId}`;
            setSelectedCoProcessors(prev => prev.filter(id => id !== departmentId));
          } else {
            // Chọn phòng ban trực tiếp cho "Xem để biết"
            newData.followers.push(nodeId);
            // Bỏ chọn khỏi các vai trò khác
            newData.mainHandler = newData.mainHandler === nodeId ? '' : newData.mainHandler;
            newData.coordinators = newData.coordinators.filter(id => id !== nodeId);
            
            // Cập nhật selectedCoProcessors - thêm phòng ban vào danh sách
            const departmentId = `dept_${nodeId}`;
            setSelectedCoProcessors(prev => {
              if (!prev.includes(departmentId)) {
                return [...prev, departmentId];
              }
              return prev;
            });
          }
        } else {
          // Xử lý checkbox cá nhân
          if (isCurrentlySelected) {
            // Bỏ chọn cá nhân
            newData.followers = newData.followers.filter(id => id !== nodeId);
            
            // Cập nhật selectedCoProcessors - xóa user
            if (node?.user?.id) {
              setSelectedCoProcessors(prev => prev.filter(id => id !== node.user!.id));
            }
          } else {
            // Bỏ chọn khỏi các loại khác trước khi chọn
            newData.mainHandler = newData.mainHandler === nodeId ? '' : newData.mainHandler;
            newData.coordinators = newData.coordinators.filter(id => id !== nodeId);
            // Chọn cá nhân
            newData.followers.push(nodeId);
            
            // Cập nhật selectedCoProcessors - thêm user
            if (node?.user?.id) {
              setSelectedCoProcessors(prev => {
                if (!prev.includes(node.user!.id)) {
                  return [...prev, node.user!.id];
                }
                return prev;
              });
            }
          }
          
          // Kiểm tra và cập nhật trạng thái phòng ban cha
          const parentDepartment = assignTreeData.find(dept => 
            dept.children?.some(child => child.id === nodeId)
          );
          
          if (parentDepartment) {
            const siblingIds = parentDepartment.children?.map(child => child.id) || [];
            const selectedSiblings = siblingIds.filter(id => newData.followers.includes(id));
            
            if (selectedSiblings.length === siblingIds.length) {
              // Tất cả cá nhân được chọn -> chọn phòng ban
              if (!newData.followers.includes(parentDepartment.id)) {
                newData.followers.push(parentDepartment.id);
              }
            } else {
              // Một số cá nhân được chọn -> bỏ chọn phòng ban (để hiển thị indeterminate)
              newData.followers = newData.followers.filter(id => id !== parentDepartment.id);
            }
          }
        }
        
        // Bỏ chọn khỏi các loại khác
        newData.mainHandler = newData.mainHandler === nodeId ? '' : newData.mainHandler;
        newData.coordinators = newData.coordinators.filter(id => id !== nodeId);
      } else if (type === 'main') {
        // Toggle xử lý chính
        const oldMainHandler = newData.mainHandler;
        
        // Lưu trạng thái "Check All" TRƯỚC khi thay đổi
        const currentUserId = getCurrentUserId();
        const userIds = assignTreeData.flatMap(node => 
          node.children ? node.children.map(child => child.id) : []
        );
        
        // Loại trừ current user khỏi danh sách
        const filteredUserIds = userIds.filter(id => {
          const userId = id.replace('user_', '');
          return userId !== currentUserId?.toString();
        });
        
        // Tính toán users cần được chọn (loại trừ người xử lý chính hiện tại)
        const expectedUserIds = oldMainHandler ? 
          filteredUserIds.filter(id => id !== oldMainHandler) : 
          filteredUserIds;
        const wasFollowersCheckAll = expectedUserIds.length > 0 && 
          expectedUserIds.every(id => newData.followers.includes(id));
        const wasCoordinatorsCheckAll = expectedUserIds.length > 0 && 
          expectedUserIds.every(id => newData.coordinators.includes(id));
        
        // Kiểm tra trạng thái "Check All" của phòng ban cũ TRƯỚC KHI thay đổi mainHandler
        let wasDepartmentFollowersCheckAll = false;
        let wasDepartmentCoordinatorsCheckAll = false;
        let oldMainHandlerDepartment = null;
        
        if (oldMainHandler) {
          oldMainHandlerDepartment = assignTreeData.find(dept => 
            dept.children?.some(child => child.id === oldMainHandler)
          );
          
          if (oldMainHandlerDepartment) {
            wasDepartmentFollowersCheckAll = isDepartmentAllFollowersSelected(oldMainHandlerDepartment.id);
            wasDepartmentCoordinatorsCheckAll = isDepartmentAllCoordinatorsSelected(oldMainHandlerDepartment.id);
          }
        }
        
        if (node?.type === 'department') {
          // Nếu chọn phòng ban, chọn trực tiếp phòng ban (không phải trưởng phòng)
          newData.mainHandler = newData.mainHandler === nodeId ? '' : nodeId;
          // Bỏ chọn khỏi các loại khác
          newData.coordinators = newData.coordinators.filter(id => id !== nodeId);
          newData.followers = newData.followers.filter(id => id !== nodeId);
        } else {
          // Chọn cá nhân trực tiếp
          newData.mainHandler = newData.mainHandler === nodeId ? '' : nodeId;
          // Bỏ chọn khỏi các loại khác
          newData.coordinators = newData.coordinators.filter(id => id !== nodeId);
          newData.followers = newData.followers.filter(id => id !== nodeId);
        }
        
        // Duy trì trạng thái "Check All" khi chuyển người xử lý chính
        if (oldMainHandler && oldMainHandler !== newData.mainHandler) {
          // Nếu cột đang "Check All", thêm lại người xử lý chính cũ (chỉ nếu không phải current user)
          const oldMainHandlerUserId = oldMainHandler.replace('user_', '');
          const isOldMainHandlerCurrentUser = oldMainHandlerUserId === currentUserId?.toString();
          
          if (oldMainHandlerDepartment) {
            // Sử dụng trạng thái "Check All" của phòng ban đã lưu trước đó
            if (wasDepartmentFollowersCheckAll && !isOldMainHandlerCurrentUser && !newData.followers.includes(oldMainHandler)) {
              newData.followers.push(oldMainHandler);
            }
            if (wasDepartmentCoordinatorsCheckAll && !isOldMainHandlerCurrentUser && !newData.coordinators.includes(oldMainHandler)) {
              newData.coordinators.push(oldMainHandler);
            }
          } else {
            // Fallback: kiểm tra "Check All" toàn bộ như cũ
            if (wasFollowersCheckAll && !isOldMainHandlerCurrentUser && !newData.followers.includes(oldMainHandler)) {
              newData.followers.push(oldMainHandler);
            }
            if (wasCoordinatorsCheckAll && !isOldMainHandlerCurrentUser && !newData.coordinators.includes(oldMainHandler)) {
              newData.coordinators.push(oldMainHandler);
            }
          }
        }
        
        // Cập nhật selectedCoProcessors khi chọn/bỏ chọn radio "Xử lý chính"
        setSelectedCoProcessors(prev => {
          // Xóa tất cả main handlers cũ (cả user và department) khỏi danh sách
          const filteredList = prev.filter(id => {
            // Xóa main handler cũ nếu có
            if (oldMainHandler) {
              // Xóa user cũ nếu là user
              if (oldMainHandler.startsWith('user_')) {
                const oldUserId = oldMainHandler.replace('user_', '');
                if (id === parseInt(oldUserId)) {
                  return false;
                }
              }
              // Xóa department cũ nếu là department
              if (oldMainHandler.startsWith('dept_')) {
                const oldDeptId = oldMainHandler.replace('dept_', '');
                if (id === `dept_${oldDeptId}`) {
                  return false;
                }
              }
            }
            return true;
          });
          
          // Thêm main handler mới nếu được chọn
          if (newData.mainHandler === nodeId) {
            if (node?.user?.id) {
              // Thêm user
              if (!filteredList.includes(node.user.id)) {
                return [...filteredList, node.user.id];
              }
            } else if (node?.type === 'department') {
              // Thêm phòng ban
              const departmentId = `dept_${nodeId}`;
              if (!filteredList.includes(departmentId)) {
                return [...filteredList, departmentId];
              }
            }
          }
          
          return filteredList;
        });
      } else if (type === 'coord') {
        // Toggle phối hợp xử lý
        let isCurrentlySelected = newData.coordinators.includes(nodeId);
        
        if (node?.type === 'department') {
          // Xử lý checkbox phòng ban - chọn trực tiếp
          if (isCurrentlySelected) {
            // Bỏ chọn phòng ban
            newData.coordinators = newData.coordinators.filter(id => id !== nodeId);
          } else {
            // Chọn phòng ban trực tiếp
            newData.coordinators.push(nodeId);
            // Bỏ chọn khỏi các loại khác
            newData.mainHandler = newData.mainHandler === nodeId ? '' : newData.mainHandler;
            newData.followers = newData.followers.filter(id => id !== nodeId);
          }
        } else {
          // Xử lý checkbox cá nhân
          if (isCurrentlySelected) {
            newData.coordinators = newData.coordinators.filter(id => id !== nodeId);
          } else {
            // Bỏ chọn khỏi các loại khác trước khi chọn
            newData.mainHandler = newData.mainHandler === nodeId ? '' : newData.mainHandler;
            newData.followers = newData.followers.filter(id => id !== nodeId);
            newData.coordinators.push(nodeId);
          }
          
          // Kiểm tra và cập nhật trạng thái phòng ban cha
          const parentDepartment = assignTreeData.find(dept => 
            dept.children?.some(child => child.id === nodeId)
          );
          
          if (parentDepartment) {
            const siblingIds = parentDepartment.children?.map(child => child.id) || [];
            const selectedSiblings = siblingIds.filter(id => newData.coordinators.includes(id));
            
            if (selectedSiblings.length === siblingIds.length) {
              // Tất cả cá nhân được chọn -> chọn phòng ban
              if (!newData.coordinators.includes(parentDepartment.id)) {
                newData.coordinators.push(parentDepartment.id);
              }
            } else {
              // Một số cá nhân được chọn -> bỏ chọn phòng ban (để hiển thị indeterminate)
              newData.coordinators = newData.coordinators.filter(id => id !== parentDepartment.id);
            }
          }
        }
        
        // Cập nhật selectedCoProcessors khi chọn/bỏ chọn checkbox "Phối hợp"
        if (node?.user?.id) {
          // Xử lý cho cá nhân
          if (newData.coordinators.includes(nodeId)) {
            // Chọn checkbox - thêm user vào danh sách
            setSelectedCoProcessors(prev => {
              if (!prev.includes(node.user!.id)) {
                return [...prev, node.user!.id];
              }
              return prev;
            });
          } else {
            // Bỏ chọn checkbox - xóa user khỏi danh sách
            setSelectedCoProcessors(prev => prev.filter(id => id !== node.user!.id));
          }
        } else if (node?.type === 'department') {
          // Xử lý cho phòng ban - thêm phòng ban vào danh sách thay vì users
          if (newData.coordinators.includes(nodeId)) {
            // Chọn phòng ban - thêm phòng ban vào danh sách
            setSelectedCoProcessors(prev => {
              const departmentId = `dept_${nodeId}`;
              if (!prev.includes(departmentId)) {
                return [...prev, departmentId];
              }
              return prev;
            });
          } else {
            // Bỏ chọn phòng ban - xóa phòng ban khỏi danh sách
            const departmentId = `dept_${nodeId}`;
            setSelectedCoProcessors(prev => prev.filter(id => id !== departmentId));
          }
        }
      }
      
      return newData;
    });
  };

  // Hàm xử lý checkbox "All" cho departments trong phần "Xem để biết"
  const handleAllDepartmentsChange = () => {
    if (isAllDepartmentsSelected) {
      // Bỏ chọn tất cả departments
      const departmentIds = assignTreeData.map(node => node.id);
      setAssignData(prev => ({
        ...prev,
        followers: prev.followers.filter(id => !departmentIds.includes(id))
      }));
    } else {
      // Chọn tất cả departments
      const departmentIds = assignTreeData.map(node => node.id);
      setAssignData(prev => ({
        ...prev,
        followers: [...prev.followers, ...departmentIds.filter(id => !prev.followers.includes(id))]
      }));
    }
    setIsAllDepartmentsSelected(!isAllDepartmentsSelected);
  };

  // Hàm xử lý checkbox "All" cho departments trong phần "Xử lý phối hợp"
  const handleAllDepartmentsCoordChange = () => {
    if (isAllDepartmentsCoordSelected) {
      // Bỏ chọn tất cả departments khỏi coordinators
      const departmentIds = assignTreeData.map(node => node.id);
      setAssignData(prev => ({
        ...prev,
        coordinators: prev.coordinators.filter(id => !departmentIds.includes(id))
      }));
    } else {
      // Chọn tất cả departments vào coordinators
      const departmentIds = assignTreeData.map(node => node.id);
      setAssignData(prev => ({
        ...prev,
        coordinators: [...prev.coordinators, ...departmentIds.filter(id => !prev.coordinators.includes(id))]
      }));
    }
    setIsAllDepartmentsCoordSelected(!isAllDepartmentsCoordSelected);
  };

  // Hàm xử lý checkbox "All" cho users
  const handleAllUsersChange = () => {
    if (isAllUsersSelected) {
      // Bỏ chọn tất cả users
      const userIds = assignTreeData.flatMap(node => 
        node.children ? node.children.map(child => child.id) : []
      );
      setAssignData(prev => ({
        ...prev,
        followers: prev.followers.filter(id => !userIds.includes(id))
      }));
    } else {
      // Chọn tất cả users (loại trừ current user)
      const currentUserId = getCurrentUserId();
      
      const userIds = assignTreeData.flatMap(node => 
        node.children ? node.children.map(child => child.id) : []
      );
      
      // Loại trừ current user khỏi danh sách
      const filteredUserIds = userIds.filter(id => {
        const userId = id.replace('user_', '');
        return userId !== currentUserId?.toString();
      });
      
      setAssignData(prev => {
        const newFollowers = [...prev.followers, ...filteredUserIds.filter(id => !prev.followers.includes(id))];
        
        // Loại bỏ user xử lý chính khỏi danh sách followers nếu có
        const finalFollowers = prev.mainHandler ? 
          newFollowers.filter(id => id !== prev.mainHandler) : 
          newFollowers;
        
        return {
          ...prev,
          followers: finalFollowers
        };
      });
    }
    setIsAllUsersSelected(!isAllUsersSelected);
  };

  // Hàm xử lý checkbox "All" cho coordinators
  const handleAllCoordinatorsChange = () => {
    if (isAllCoordinatorsSelected) {
      // Bỏ chọn tất cả coordinators (chỉ cá nhân, không bao gồm phòng ban)
      const userIds = assignTreeData.flatMap(node => 
        node.children ? node.children.map(child => child.id) : []
      );
      setAssignData(prev => ({
        ...prev,
        coordinators: prev.coordinators.filter(id => !userIds.includes(id))
      }));
    } else {
      // Chọn tất cả coordinators (chỉ cá nhân, không bao gồm phòng ban, loại trừ current user)
      const currentUserId = getCurrentUserId();
      
      const userIds = assignTreeData.flatMap(node => 
        node.children ? node.children.map(child => child.id) : []
      );
      
      // Loại trừ current user khỏi danh sách
      const filteredUserIds = userIds.filter(id => {
        const userId = id.replace('user_', '');
        return userId !== currentUserId?.toString();
      });
      
      setAssignData(prev => {
        const newCoordinators = [...prev.coordinators, ...filteredUserIds.filter(id => !prev.coordinators.includes(id))];
        
        // Loại bỏ user xử lý chính khỏi danh sách coordinators nếu có
        const finalCoordinators = prev.mainHandler ? 
          newCoordinators.filter(id => id !== prev.mainHandler) : 
          newCoordinators;
        
        return {
          ...prev,
          coordinators: finalCoordinators
        };
      });
    }
    setIsAllCoordinatorsSelected(!isAllCoordinatorsSelected);
  };

  const handleOpenYKienMau = () => {
    setYKienMauOpen(true);
  };

  const handleCloseYKienMau = () => {
    setYKienMauOpen(false);
  };

  const handleTemplateSelect = (templateContent: string) => {
    setFormData(prev => ({
      ...prev,
      processingContent: templateContent
    }));
    setYKienMauOpen(false);
  };

  // Fetch personal groups
  const fetchPersonalGroups = async () => {
    setPersonalGroupsLoading(true);
    try {
      const response = await axiosInstance.get('/personal-groups');
      if (response.data.code === 0) {
        const items = response.data.data?.items;
        // Đảm bảo items là array
        if (Array.isArray(items)) {
          setPersonalGroups(items);
        } else {

          setPersonalGroups([]);
        }
      } else {

        setPersonalGroups([]);
      }
    } catch (error) {

      setPersonalGroups([]);
    } finally {
      setPersonalGroupsLoading(false);
    }
  };

  // Handle quick selection change
  const handleQuickSelectionChange = (value: string) => {
    setQuickSelection(value);
    setSelectedPersonalGroup(''); // Reset personal group selection
    
    // Clear current selections
    setAssignData({
      mainHandler: '',
      coordinators: [],
      followers: []
    });

    switch (value) {
      case 'lanh_dao_ban':
        handleLanhDaoBanSelection();
        break;
      case 'lanh_dao_ban_phong':
        handleLanhDaoBanPhongSelection();
        break;
      case 'truong_phong':
        handleTruongPhongSelection();
        break;
      case 'cung_phong':
        handleCungPhongSelection();
        break;
      case 'nhom_ca_nhan':
        if (personalGroups.length === 0) {
          fetchPersonalGroups();
        }
        break;
      default:
        break;
    }
  };

  // Lãnh đạo ban selection
  const handleLanhDaoBanSelection = () => {
    const lanhDaoBanNode = assignTreeData.find(node => node.name === 'Lãnh đạo ban');
    if (lanhDaoBanNode) {
      // Expand Lãnh đạo ban node
      setAssignTreeData(prev => 
        prev.map(node => 
          node.name === 'Lãnh đạo ban' 
            ? { ...node, expanded: true }
            : { ...node, expanded: false }
        )
      );
      
      // Select all users in Lãnh đạo ban
      const lanhDaoBanUserIds = lanhDaoBanNode.children?.map(child => child.id) || [];
      setAssignData(prev => ({
        ...prev,
        followers: [lanhDaoBanNode.id, ...lanhDaoBanUserIds]
      }));
    }
  };

  // Lãnh đạo ban và các phòng selection
  const handleLanhDaoBanPhongSelection = () => {
    const selectedIds: string[] = [];
    
    assignTreeData.forEach(node => {
      if (node.name === 'Lãnh đạo ban') {
        // Select all users in Lãnh đạo ban
        selectedIds.push(node.id);
        if (node.children) {
          selectedIds.push(...node.children.map(child => child.id));
        }
      } else {
        // Select only trưởng phòng (first user in each department)
        if (node.children && node.children.length > 0) {
          const truongPhong = node.children.find(child => {
            const position = child.user?.position?.toLowerCase() || '';
            return position.includes('trưởng phòng') && !position.includes('phó');
          });
          if (truongPhong) {
            selectedIds.push(truongPhong.id);
          }
        }
      }
    });
    
    setAssignData(prev => ({
      ...prev,
      followers: selectedIds
    }));
  };

  // Trưởng phòng selection
  const handleTruongPhongSelection = () => {
    const selectedIds: string[] = [];
    
    // Đóng tất cả các nhánh đang expanded
    setAssignTreeData(prev => 
      prev.map(node => ({
        ...node,
        expanded: false
      }))
    );
    
    assignTreeData.forEach(node => {
      if (node.children) {
        const truongPhong = node.children.find(child => {
          const position = child.user?.position?.toLowerCase() || '';
          return position.includes('trưởng phòng') && !position.includes('phó');
        });
        if (truongPhong) {
          selectedIds.push(truongPhong.id);
        }
      }
    });
    
    setAssignData(prev => ({
      ...prev,
      followers: selectedIds
    }));
  };

  // Cùng phòng selection (cần thông tin user hiện tại)
  const handleCungPhongSelection = () => {
    // Lấy thông tin user hiện tại
    const currentUser = getCurrentUser();
    const userDepartment = currentUser?.department;
    
    if (userDepartment) {
      // Tìm phòng ban của user trong tree data
      const userDepartmentNode = assignTreeData.find(node => node.name === userDepartment);
      
      if (userDepartmentNode) {
        // Expand only user's department, collapse others
        setAssignTreeData(prev => 
          prev.map(node => ({
            ...node,
            expanded: node.id === userDepartmentNode.id
          }))
        );
        
        // Select all users in user's department
        const departmentUserIds = userDepartmentNode.children?.map(child => child.id) || [];
        setAssignData(prev => ({
          ...prev,
          followers: [userDepartmentNode.id, ...departmentUserIds]
        }));
      }
    }
  };

  // Handle personal group selection
  const handlePersonalGroupChange = (groupId: string) => {
    setSelectedPersonalGroup(groupId);
    
    if (!groupId) {
      // Clear selections if no group selected
      setAssignData(prev => ({
        ...prev,
        followers: []
      }));
      return;
    }
    
    const selectedGroup = personalGroups.find(group => {
      const groupIdStr = group.id.toString();
      const match = groupIdStr === groupId || group.id === parseInt(groupId);
      return match;
    });
    
    if (!selectedGroup) {
      // Clear selections if group not found
      setAssignData(prev => ({
        ...prev,
        followers: []
      }));
      return;
    }
    
    if (!selectedGroup.member_details || !Array.isArray(selectedGroup.member_details)) {

      // Clear selections if no member_details
      setAssignData(prev => ({
        ...prev,
        followers: []
      }));
      return;
    }

    // Lưu thông tin nhóm để sử dụng trong dialog
    setSelectedPersonalGroup(groupId);
    setSelectedDepartmentName(selectedGroup.group_name);
    setSelectedDepartmentId('personal_group_' + groupId);
    setDepartmentRoleDialogOpen(true);
  };

  // Function để xử lý chọn vai trò cho nhóm cá nhân
  const handlePersonalGroupRoleSelect = (role: 'coord' | 'follow') => {
    const groupId = selectedPersonalGroup;
    const selectedGroup = personalGroups.find(group => {
      const groupIdStr = group.id.toString();
      const match = groupIdStr === groupId || group.id === parseInt(groupId);
      return match;
    });
    
    if (!selectedGroup || !selectedGroup.member_details) return;

    // Map group users to tree user IDs using member_details
    const selectedUserIds: string[] = [];
    // Lưu các user IDs thực tế (số nguyên) để thêm vào selectedCoProcessors
    const actualUserIds: number[] = [];
    
    selectedGroup.member_details.forEach((member: any) => {    
      // Find matching user in assignTreeData
      assignTreeData.forEach(node => {
        if (node.children) {
          const matchingUser = node.children.find(child => {           
            // The tree node ID is in format "user_${user.id}"
            const expectedTreeId = `user_${member.id}`;
            const treeIdMatch = child.id === expectedTreeId;
            
            // Also try other matching strategies as backup
            const userIdMatch = child.user?.id === member.id;
            const usernameMatch = child.user?.username === member.username;
            const fullnameMatch = child.user?.fullname === member.fullname;
            const emailMatch = child.user?.email === member.email;
                        
            return treeIdMatch || userIdMatch || usernameMatch || fullnameMatch || emailMatch;
          });
          
          if (matchingUser) {
            selectedUserIds.push(matchingUser.id);
            // Lưu user ID thực tế (số nguyên) để thêm vào selectedCoProcessors
            if (matchingUser.user?.id && !actualUserIds.includes(matchingUser.user.id)) {
              actualUserIds.push(matchingUser.user.id);
            }
          }
        }
      });
    });

    // Update selections based on role
    setAssignData(prev => {
      const newData = { ...prev };
      
      if (role === 'coord') {
        // Chọn nhóm cá nhân làm phối hợp
        const uniqueCoordinators = Array.from(new Set([...newData.coordinators, ...selectedUserIds]));
        newData.coordinators = uniqueCoordinators;
        // Xóa khỏi vai trò khác
        newData.followers = newData.followers.filter(id => !selectedUserIds.includes(id));
      } else if (role === 'follow') {
        // Chọn nhóm cá nhân làm xem để biết
        const uniqueFollowers = Array.from(new Set([...newData.followers, ...selectedUserIds]));
        newData.followers = uniqueFollowers;
        // Xóa khỏi vai trò khác
        newData.coordinators = newData.coordinators.filter(id => !selectedUserIds.includes(id));
      }
      
      return newData;
    });

    // Cập nhật selectedCoProcessors với các user IDs thực tế
    setSelectedCoProcessors(prev => {
      const newList = [...prev];
      actualUserIds.forEach(userId => {
        if (!newList.includes(userId)) {
          newList.push(userId);
        }
      });
      return newList;
    });

    setDepartmentRoleDialogOpen(false);

    // Force update the "All" checkboxes state after a short delay
    // to ensure the assignData state has been updated
    setTimeout(() => {
      const departmentIds = assignTreeData.map(node => node.id);
      const allDepartmentsSelected = departmentIds.length > 0 && 
        departmentIds.every(id => selectedUserIds.includes(id));
      setIsAllDepartmentsSelected(allDepartmentsSelected);

      const userIds = assignTreeData.flatMap(node => 
        node.children ? node.children.map(child => child.id) : []
      );
      const allUsersSelected = userIds.length > 0 && 
        userIds.every(id => selectedUserIds.includes(id));
      setIsAllUsersSelected(allUsersSelected);
    }, 200);
  };

  // Render assign tree node
  const renderAssignTreeNode = (node: AssignTreeNode, index: number) => {
    const isDepartment = node.type === 'department';
    
    // Lấy thông tin user hiện tại để kiểm tra disable
    const currentUserId = getCurrentUserId();
    
    // Kiểm tra xem node này có phải là current user không
    const isCurrentUser = !isDepartment && node.user?.id === currentUserId;
    
    // Tính toán trạng thái selected cho radio button
    let isSelected = false;
    if (isDepartment) {
      // Phòng ban có thể được selected khi chọn làm người xử lý chính
      isSelected = assignData.mainHandler === node.id;
    } else {
      // Với cá nhân, kiểm tra trực tiếp
      isSelected = assignData.mainHandler === node.id;
    }
    
    // Tính toán trạng thái coordinator
    let isCoordinator = false;
    if (isDepartment) {
      // Với phòng ban, kiểm tra xem phòng ban có được chọn làm coordinator không
      isCoordinator = assignData.coordinators.includes(node.id);
    } else {
      isCoordinator = assignData.coordinators.includes(node.id);
    }

    // Tính toán trạng thái follower
    let isFollower = false;
    if (isDepartment) {
      // Với phòng ban, kiểm tra xem phòng ban có được chọn làm follower không
      isFollower = assignData.followers.includes(node.id);
    } else {
      isFollower = assignData.followers.includes(node.id);
    }
    
    // Tính toán trạng thái indeterminate cho phòng ban
    let isIndeterminate = false;
    // Phòng ban không có trạng thái indeterminate vì nó độc lập với users

    // Kiểm tra xem user này có được highlight không
    const isHighlighted = !isDepartment && highlightedUsers.includes(node.id);
    
    // Kiểm tra xem user này có đang được hover từ danh sách không
    const isHoveredFromList = !isDepartment && node.user?.id === hoveredUserId;

    return (
      <TableRow 
        key={node.id}
        id={!isDepartment ? `user-row-${node.id}` : undefined}
        sx={{ 
          backgroundColor: isHighlighted 
            ? '#fff3cd' // Màu vàng nhạt cho highlight
            : isHoveredFromList
            ? '#e3f2fd' // Màu xanh nhạt cho hover từ danh sách
            : (index % 2 === 0 ? '#f8f9fa' : '#ffffff'),
          '&:hover': { backgroundColor: isHighlighted ? '#ffeaa7' : isHoveredFromList ? '#bbdefb' : '#f5f5f5' },
          border: isHighlighted ? '2px solid #ffc107' : isHoveredFromList ? '2px solid #1976d2' : 'none',
          borderRadius: (isHighlighted || isHoveredFromList) ? '4px' : '0px'
        }}
      >
        {/* Cột Đơn vị */}
        <TableCell 
          sx={{ 
            paddingLeft: `${1 + node.level * 4}px !important`,
            borderRight: '1px solid #dee2e6',
            width: '37.5%',
            backgroundColor: 'inherit',
            padding: '2px 4px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isDepartment && (
              <IconButton
                size="small"
                onClick={() => toggleAssignNode(node.id)}
                sx={{ 
                  padding: 0.5,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                }}
              >
                {node.expanded ? 
                  <RemoveBoxIcon sx={{ verticalAlign: 'top', fontSize: '18px', color: '#000000de' }} /> : 
                  <AddBoxIcon sx={{ verticalAlign: 'top', fontSize: '18px', color: '#000000de' }} />
                }
              </IconButton>
            )}
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '12px',
                cursor: isDepartment ? 'pointer' : 'default'
              }}
              onClick={() => {
                if (isDepartment) {
                  toggleAssignNode(node.id);
                }
              }}
            >
              {isDepartment ? node.name : `${node.name} - ${node.user?.position_label || node.user?.position || ''}`}
            </Typography>
          </Box>
        </TableCell>

        {/* Cột Xử lý chính - ẩn trong chế độ Thêm người phối hợp */}
        {!isThemNguoiPhoiHopMode && (
          <TableCell 
            align="center" 
            sx={{ 
              borderRight: '1px solid #dee2e6',
              width: '12.5%',
              backgroundColor: 'inherit',
              padding: '2px 2px'
            }}
          >
            <FormControlLabel
              control={
                <Radio
                  checked={isSelected}
                  onChange={() => handleAssignSelectType(node.id, 'main')}
                  disabled={isCurrentUser}
                  size="small"
                  sx={{ 
                    transform: 'scale(1.1)',
                    '& .MuiSvgIcon-root': { fontSize: '1.2rem' }
                  }}
                />
              }
              label=""
              sx={{ margin: 0 }}
            />
          </TableCell>
        )}

        {/* Cột Xử lý phối hợp - P.ban */}
        <TableCell 
          align="center" 
          sx={{ 
            borderRight: '1px solid #dee2e6',
            width: '12.5%',
            backgroundColor: 'inherit',
            padding: '4px 8px',
            verticalAlign: 'middle'
          }}
        >
          {isDepartment && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCoordinator}
                  onChange={() => handleAssignSelectType(node.id, 'coord')}
                  size="small"
                  sx={{ 
                    transform: 'scale(1.1)',
                    '& .MuiSvgIcon-root': { fontSize: '1.2rem' }
                  }}
                />
              }
              label=""
              sx={{ 
                margin: 0,
                justifyContent: 'flex-end',
                marginRight: '8px'
              }}
            />
          )}
        </TableCell>

        {/* Cột Xử lý phối hợp - C.Nhân */}
        <TableCell 
          align="center" 
          sx={{ 
            borderRight: '1px solid #dee2e6',
            width: '12.5%',
            backgroundColor: 'inherit',
            padding: '4px 8px',
            verticalAlign: 'middle'
          }}
        >
          {isDepartment ? (
            // Checkbox "Check All" cho phòng ban - chỉ hiển thị khi expand
            node.expanded ? (
              <Tooltip title="Chọn hết người dùng trong phòng" placement="top">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDepartmentAllCoordinatorsSelected(node.id)}
                  indeterminate={isDepartmentIndeterminateCoordinators(node.id)}
                  onChange={() => handleDepartmentAllCoordinatorsChange(node.id)}
                  size="small"
                  sx={{ 
                    transform: 'scale(1.1)',
                    '& .MuiSvgIcon-root': { fontSize: '1.2rem' }
                  }}
                />
              }
              label=""
              sx={{ margin: 0 }}
            />
              </Tooltip>
            ) : null
          ) : (
            // Checkbox thông thường cho cá nhân
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCoordinator}
                  onChange={() => handleAssignSelectType(node.id, 'coord')}
                  disabled={isCurrentUser}
                  size="small"
                  sx={{ 
                    transform: 'scale(1.1)',
                    '& .MuiSvgIcon-root': { fontSize: '1.2rem' }
                  }}
                />
              }
              label=""
              sx={{ 
                margin: 0,
                justifyContent: 'flex-end',
                marginRight: '8px'
              }}
            />
          )}
        </TableCell>

        {/* Cột Xem để biết - P.ban */}
        <TableCell 
          align="center" 
          sx={{ 
            borderRight: '1px solid #dee2e6',
            width: '12.5%',
            backgroundColor: 'inherit',
            padding: '4px 8px',
            verticalAlign: 'middle'
          }}
        >
          {isDepartment && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isFollower}
                  indeterminate={isIndeterminate}
                  onChange={() => handleAssignSelectType(node.id, 'follow')}
                  size="small"
                  sx={{ 
                    transform: 'scale(1.1)',
                    '& .MuiSvgIcon-root': { fontSize: '1.2rem' }
                  }}
                />
              }
              label=""
              sx={{ 
                margin: 0,
                justifyContent: 'flex-end',
                marginRight: '8px'
              }}
            />
          )}
        </TableCell>

        {/* Cột Xem để biết - C.Nhân */}
        <TableCell 
          align="center" 
          sx={{ 
            width: '12.5%',
            backgroundColor: 'inherit',
            padding: '4px 8px',
            verticalAlign: 'middle'
          }}
        >
          {isDepartment ? (
            // Checkbox "Check All" cho phòng ban - chỉ hiển thị khi expand
            node.expanded ? (
              <Tooltip title="Chọn hết người dùng trong phòng" placement="top">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDepartmentAllFollowersSelected(node.id)}
                  indeterminate={isDepartmentIndeterminateFollowers(node.id)}
                  onChange={() => handleDepartmentAllFollowersChange(node.id)}
                  size="small"
                  sx={{ 
                    transform: 'scale(1.1)',
                    '& .MuiSvgIcon-root': { fontSize: '1.2rem' }
                  }}
                />
              }
              label=""
              sx={{ margin: 0 }}
            />
              </Tooltip>
            ) : null
          ) : (
            // Checkbox thông thường cho cá nhân
            <FormControlLabel
              control={
                <Checkbox
                  checked={isFollower}
                  onChange={() => handleAssignSelectType(node.id, 'follow')}
                  disabled={isCurrentUser}
                  size="small"
                  sx={{ 
                    transform: 'scale(1.1)',
                    '& .MuiSvgIcon-root': { fontSize: '1.2rem' }
                  }}
                />
              }
              label=""
              sx={{ 
                margin: 0,
                justifyContent: 'flex-end',
                marginRight: '8px'
              }}
            />
          )}
        </TableCell>
      </TableRow>
    );
  };


  const validateForm = () => {
    if (!formData.note.trim()) {
      setError('Vui lòng nhập ghi chú');
      return false;
    }
    return true;
  };

  const isThemNguoiPhoiHopMode = !!action && action.includes('them-xuly-phoihop');

  // Hàm kiểm tra vai trò hiện tại của user có phải là "Phối hợp" không
  const isCurrentUserCoordinator = (): boolean => {
    if (!documentData) return false;

    const checkVaiTro = (doc: any): boolean => {
      // Kiểm tra vai_tro field (đã được map từ action_needed)
      if (doc.vai_tro === 'PH') {
        return true;
      }
      // Kiểm tra action_needed field (giá trị gốc)
      if (doc.action_needed === 'xuly_phoihop' || doc.action_needed === 'xuly_phoihop_ph') {
        return true;
      }
      // Kiểm tra prev_action.action_needed (từ API chi tiết văn bản)
      if (doc.prev_action && doc.prev_action.action_needed === 'xuly_phoihop') {
        return true;
      }
      return false;
    };

    // Xử lý trường hợp một văn bản
    if (!Array.isArray(documentData)) {
      return checkVaiTro(documentData);
    }

    // Xử lý trường hợp nhiều văn bản - kiểm tra nếu có ít nhất một văn bản có vai trò PH
    return documentData.some(doc => checkVaiTro(doc));
  };

  const handleSubmit = async () => {
    // Kiểm tra vai trò hiện tại của user
    const isCoordinator = isCurrentUserCoordinator();
    
    // Validation: Nếu KHÔNG phải chế độ "Thêm người phối hợp" và vai trò không phải "Phối hợp"
    // thì bắt buộc phải chọn người xử lý chính
    if (!isThemNguoiPhoiHopMode && !isCoordinator && !assignData.mainHandler) {
      setError('Vui lòng chọn người xử lý chính');
      return;
    }

    // Kiểm tra có dữ liệu văn bản không
    if (!documentData && !documentIds && !documentId) {
      setError('Không tìm thấy văn bản để chuyển xử lý');
      return;
    }

    if (!action) {
      setError('Chưa có api');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      // Xử lý user xử lý chính
      let mainHandlerUserId: number | null = null;
      
      // Nếu có chọn người xử lý chính, thì validate và lấy ID
      if (assignData.mainHandler) {
        const mainHandlerId = getDepartmentHeadId(assignData.mainHandler);
        
        if (!mainHandlerId || !mainHandlerId.startsWith('user_')) {
          setError('Vui lòng chọn người xử lý chính hợp lệ');
          return;
        }
        
        const parsedUserId = parseInt(mainHandlerId.replace('user_', ''), 10);
        
        if (isNaN(parsedUserId)) {
          setError('Vui lòng chọn người xử lý chính hợp lệ');
          return;
        }
        
        mainHandlerUserId = parsedUserId;
      }
      // Nếu không có mainHandler và vai trò là "Phối hợp", thì cho phép mainHandlerUserId = null

      // Xử lý user phối hợp
      const coordinatorIds = assignData.coordinators
        .filter(id => id != null && id !== '' && id !== undefined)
        .map(id => getDepartmentHeadId(id)) // Chuyển đổi department ID thành trưởng phòng ID
        .filter((id): id is string => id !== null && typeof id === 'string' && id.startsWith('user_'))
        .map(id => {
          const userIdStr = id.replace('user_', '');
          const numId = parseInt(userIdStr, 10);
          return isNaN(numId) ? null : numId;
        })
        .filter((id): id is number => id !== null);

      // Xử lý user xem để biết
      const followerIds = assignData.followers
        .filter(id => id != null && id !== '' && id !== undefined)
        .map(id => getDepartmentHeadId(id)) // Chuyển đổi department ID thành trưởng phòng ID
        .filter((id): id is string => id !== null && typeof id === 'string' && id.startsWith('user_'))
        .map(id => {
          const userIdStr = id.replace('user_', '');
          const numId = parseInt(userIdStr, 10);
          return isNaN(numId) ? null : numId;
        })
        .filter((id): id is number => id !== null);

      // Chuẩn bị doc_ids - xử lý cả trường hợp một văn bản và nhiều văn bản
      let docIds: number[] = [];
      if (documentIds && documentIds.length > 0) {
        // Sử dụng documentIds từ props (nhiều văn bản)
        docIds = documentIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
      } else if (Array.isArray(documentData)) {
        // Nhiều văn bản từ documentData
        docIds = documentData.map(doc => doc.id).filter(id => id != null);
      } else if (documentData && documentData.id) {
        // Một văn bản từ documentData
        docIds = [documentData.id];
      } else if (documentId) {
        // Fallback sử dụng documentId
        const numId = parseInt(documentId, 10);
        if (!isNaN(numId)) {
          docIds = [numId];
        }
      }

      if (docIds.length === 0) {
        setError('Không tìm thấy ID văn bản hợp lệ');
        return;
      }

      // Chuẩn bị dữ liệu gửi lên API theo format mới
      const requestData: any = {
        doc_ids: docIds,
        user_id_xuly_chinh: mainHandlerUserId, // Có thể là null nếu vai trò là "Phối hợp"
        user_ids_xuly_phoihop: coordinatorIds,
        user_ids_xemdebiet: followerIds,
        note: formData.processingContent.trim() || undefined
      };

      // Sử dụng action từ props nếu có, nếu không thì dùng endpoint mặc định
      // Loại bỏ /api prefix nếu có vì axiosInstance đã có baseURL chứa /api
      let apiEndpoint = '/xlvb/incoming-documents/chuyen-xu-ly'; // Fallback mặc định
      if (action) {
        // Loại bỏ /api/ prefix nếu có
        apiEndpoint = action.replace(/^\/api\//, '');
      }
      
      const response = await axiosInstance.post(apiEndpoint, requestData);
      console.log('[ChuyenXuLyPopup] POST response', { code: response.data?.code, message: response.data?.message, data: response.data?.data });

      if (response.data.code === 0) {
        // Sử dụng message từ backend (đã được format sẵn)
        const message = response.data.message || 'Chuyển xử lý thành công';
        const data = response.data.data;
        console.log('[ChuyenXuLyPopup] API success, code=0', { documentId, documentIds, hasOnSuccess: !!onSuccess });
        
        // Kiểm tra nếu có văn bản thất bại
        if (data && data.documents_failed && data.documents_failed.length > 0) {
          // Hiển thị thông báo chính
          enqueueSnackbar(message, { variant: 'warning' });
          
          // Hiển thị chi tiết các văn bản thất bại
          data.documents_failed.forEach((failedDoc: any) => {
            enqueueSnackbar(
              `Văn bản ${failedDoc.doc_id}: ${failedDoc.error}`, 
              { variant: 'error' }
            );
          });
          
          // Nếu có văn bản thành công, vẫn refresh danh sách và đóng box chi tiết
          if (data.overall_summary && data.overall_summary.successful_documents > 0) {
            const idToPass = documentId || (documentIds && documentIds[0]);
            const isSingleDoc = !documentIds || documentIds.length <= 1;
            console.log('[ChuyenXuLyPopup] calling onSuccess (partial success)', { idToPass });
            onSuccess?.({ documentIdJustProcessed: idToPass, suggestedNextDocumentId: isSingleDoc ? (suggestedNextDocumentId ?? undefined) : undefined });
            onClose();
          } else {
            // Không có văn bản nào thành công, chỉ đóng popup
            onClose();
          }
        } else {
          // Tất cả văn bản đều thành công
          enqueueSnackbar(message, { variant: 'success' });
          
          // Hiển thị thông tin chi tiết nếu có
          if (data && data.overall_summary) {
            const summary = data.overall_summary;
            enqueueSnackbar(
              `Đã xử lý ${summary.total_users_processed} người dùng cho ${summary.successful_documents} văn bản`, 
              { variant: 'info' }
            );
          }
          
          const idToPass = documentId || (documentIds && documentIds[0]);
          const isSingleDoc = !documentIds || documentIds.length <= 1;
          console.log('[ChuyenXuLyPopup] calling onSuccess (all success)', { idToPass });
          onSuccess?.({ documentIdJustProcessed: idToPass, suggestedNextDocumentId: isSingleDoc ? (suggestedNextDocumentId ?? undefined) : undefined });
          onClose();
        }
      } else {
        // API trả về lỗi
        console.log('[ChuyenXuLyPopup] API code !== 0', response.data);
        setError(response.data.message || 'Có lỗi xảy ra khi chuyển xử lý');
      }
    } catch (error: any) {

      // Xử lý error message từ backend
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.response?.status === 400) {
        setError('Yêu cầu không hợp lệ. Vui lòng kiểm tra lại thông tin.');
      } else if (error.response?.status === 401) {
        setError('Bạn không có quyền thực hiện hành động này.');
      } else if (error.response?.status === 500) {
        setError('Lỗi server. Vui lòng thử lại sau.');
      } else {
        setError(error.message || 'Có lỗi xảy ra khi chuyển xem để biết');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData({
        note: '',
        priority: 'normal',
        deadline: '',
        processingContent: ''
      });
      setError(null);
      setSelectedMainProcessor(null);
      setSelectedCoProcessors([]);
      setIsAllCoProcessorsSelected(false);
      setAssignUsers([]);
      setAssignTreeData([]);
      setAssignData({
        mainHandler: '',
        coordinators: [],
        followers: []
      });
      setIsAllDepartmentsSelected(false);
      setIsAllDepartmentsCoordSelected(false);
      setIsAllUsersSelected(false);
      setIsAllCoordinatorsSelected(false);
      setShowFiles(false);
      setYKienMauOpen(false);
      setQuickSelection('');
      setSelectedPersonalGroup('');
      setPersonalGroups([]);
      setCurrentUserInfo(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      fullScreen
      PaperProps={{
        sx: {
          borderRadius: 0,
          boxShadow: 'none',
          height: '100vh',
          width: '100vw',
          maxHeight: '100vh',
          maxWidth: '100vw'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: 'primary.main',
        color: 'white',
        fontWeight: 500,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: 51,
        padding: '12px 8px',
        boxSizing: 'border-box'
      }}>
        <Typography component="span" variant="h6" sx={{ color: 'white', fontWeight: 500 }}>
          Chuyển xử lý văn bản
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ 
        p: 2, 
        position: 'relative',
        height: 'calc(100vh - 120px)', // Trừ đi chiều cao header và footer
        overflow: 'auto'
      }}>
        {/* Error message ở góc trái dưới */}
        {error && (
          <Box sx={{ 
            position: 'absolute',
            bottom: 8,
            left: 8,
            zIndex: 1000,
            backgroundColor: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: 1,
            px: 2,
            py: 1,
            maxWidth: '400px',
            animation: 'fadeIn 0.3s ease-in',
            '@keyframes fadeIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(10px)'
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)'
              }
            }
          }}>
            <Typography color="error" sx={{ fontSize: '12px' }}>
              {error}
            </Typography>
          </Box>
        )}
        
        <Grid container spacing={2} sx={{ minHeight: '500px' }}>
            {/* Bên trái - Grid 8: DocumentInfoAndFiles + Form chuyển xử lý */}
            <Grid item xs={8}>
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                height: '100%'
              }}>
              {/* DocumentInfoAndFiles - Chỉ hiển thị thông tin văn bản */}
              <Box>
                <DocumentInfoAndFiles 
                  documentData={documentData}
                  showPreview={false}
                  showOnlyInfo={true}
                />
              </Box>

              {/* Hiển thị hành động trước đó - chỉ khi chuyển 1 văn bản */}
              {(!documentIds || documentIds.length === 1) && documentData?.prev_action && (
                <Box sx={{ 
                  mb: 2, 
                  p: 1.5, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  borderRadius: 1
                }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                    <strong>Hành động trước đó:</strong>
                  </Typography>
                  {documentData.prev_action?.note ? (
                    <>
                      <Typography variant="body2" sx={{ color: '#333', mb: 0.5 }}>
                        {documentData.prev_action.note}
                      </Typography>
                      {documentData.prev_action.created_at && (
                        <Typography variant="caption" sx={{ color: '#999' }}>
                          <em>{new Date(documentData.prev_action.created_at).toLocaleString('vi-VN')}</em>
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#999' }}>
                      <em>Không có hành động trước đó</em>
                    </Typography>
                  )}
                </Box>
              )}

              {/* Nội dung xử lý */}
              <Box>
                <Autocomplete
                  freeSolo
                  options={noteSuggestions}
                  value={formData.processingContent}
                  onInputChange={(event, newValue, reason) => {
                    // Chỉ cập nhật khi user nhập hoặc clear
                    if (reason === 'input' || reason === 'clear') {
                      handleInputChange('processingContent', newValue || '');
                    }
                  }}
                  onChange={(event, newValue) => {
                    // Khi chọn một option từ danh sách
                    if (typeof newValue === 'string') {
                      handleInputChange('processingContent', newValue);
                    } else if (newValue === null) {
                      handleInputChange('processingContent', '');
                    }
                  }}
                  loading={noteSuggestionsLoading}
                  disabled={submitting}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={4}
                      placeholder="Nhập nội dung ghi chú..."
                      variant="outlined"
                      sx={{
                        '& .MuiInputBase-root': {
                          height: 'auto',
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {noteSuggestionsLoading ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option}>
                      <Typography variant="body2" sx={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                        {option}
                      </Typography>
                    </Box>
                  )}
                  ListboxProps={{
                    style: {
                      maxHeight: '200px',
                    },
                  }}
                />
                {/* Nút Mẫu nhập */}
                <Box sx={{ mt: 0.5, mb: 0.5,  display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleOpenYKienMau}
                    sx={{
                      fontSize: '12px',
                      textTransform: 'none',
                      color: '#1976d2',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)'
                      }
                    }}
                  >
                    (Mẫu nhập)
                  </Button>
                </Box>
              </Box>


              {/* Block Đồng gửi - Độ rộng 100% */}
              <Box sx={{ mt: 0 }}>
                <Box sx={{  
                  borderRadius: 1,
                  minHeight: '200px'
                }}>
                  {/* Header bảng */}
                  <Box sx={{ 
                    backgroundColor: '#f5f5f5', 
                    p: 1, 
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '13px' }}>
                      Phân công xử lý
                    </Typography>
                  </Box>
                  
                  {/* Bảng assign data */}
                  <Box>

                    {assignLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <TableContainer component={Paper} sx={{ overflowX: 'hidden' }}>
                        <Table size="small" stickyHeader sx={{ width: '100%', tableLayout: 'fixed' }}>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell 
                                rowSpan={2}
                                align="center" 
                                sx={{ 
                                  fontWeight: 600, 
                                  fontSize: '11px',
                                  borderRight: '1px solid #dee2e6',
                                  width: '37.5%',
                                  backgroundColor: '#f5f5f5',
                                  padding: '1px 2px',
                                  height: '12px'
                                }}
                              >
                                <TextField
                                  size="small"
                                  placeholder="Tìm kiếm user..."
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      handleSearch();
                                    }
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Tooltip title="Ấn để tìm kiếm">
                                          <IconButton
                                            size="medium"
                                            onClick={handleSearch}
                                            disabled={!searchTerm.trim() || searchLoading}
                                            sx={{
                                              padding: '6px',
                                              borderRadius: '4px',
                                              minWidth: '32px',
                                              minHeight: '32px',
                                              '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                              }
                                            }}
                                          >
                                            {searchLoading ? (
                                              <CircularProgress size={16} />
                                            ) : (
                                              <SearchIcon sx={{ fontSize: '18px', color: '#666' }} />
                                            )}
                                          </IconButton>
                                        </Tooltip>
                                        {searchTerm && (
                                          <Tooltip title="Hủy tìm kiếm">
                                            <IconButton
                                              size="medium"
                                              onClick={handleClearSearch}
                                              sx={{
                                                padding: '6px',
                                                borderRadius: '4px',
                                                minWidth: '32px',
                                                minHeight: '32px',
                                                '&:hover': {
                                                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                }
                                              }}
                                            >
                                              <CloseIcon sx={{ fontSize: '16px', color: '#666' }} />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    )
                                  }}
                                  sx={{
                                    width: '100%',
                                    '& .MuiOutlinedInput-root': {
                                      height: '20px',
                                      fontSize: '11px',
                                      '& fieldset': {
                                        borderColor: '#ccc',
                                        borderWidth: '1px'
                                      },
                                      '&:hover fieldset': {
                                        borderColor: '#999',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#1976d2',
                                        borderWidth: '1px',
                                      }
                                    },
                                    '& .MuiInputBase-input': {
                                      padding: '2px 6px',
                                      fontSize: '11px'
                                    }
                                  }}
                                />
                              </TableCell>
                              {!isThemNguoiPhoiHopMode && (
                                <TableCell 
                                  rowSpan={2}
                                  align="center" 
                                  sx={{ 
                                    fontWeight: 600, 
                                    fontSize: '11px',
                                    borderRight: '1px solid #dee2e6',
                                    width: '12.5%',
                                    backgroundColor: '#f5f5f5',
                                    padding: '1px 2px',
                                    height: '12px'
                                  }}
                                >
                                  Xử lý chính
                                </TableCell>
                              )}
                              <TableCell 
                                colSpan={2}
                                align="center" 
                                sx={{ 
                                  fontWeight: 600, 
                                  fontSize: '11px',
                                  borderRight: '1px solid #dee2e6',
                                  backgroundColor: '#f5f5f5',
                                  padding: '1px 2px',
                                  height: '12px',
                                  width: '25%'
                                }}
                              >
                                Xử lý phối hợp
                              </TableCell>
                              <TableCell 
                                colSpan={2}
                                align="center" 
                                sx={{ 
                                  fontWeight: 600, 
                                  fontSize: '11px',
                                  backgroundColor: '#f5f5f5',
                                  padding: '1px 2px',
                                  height: '12px',
                                  width: '25%'
                                }}
                              >
                                Xem để biết
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell 
                                align="center" 
                                sx={{ 
                                  fontSize: '10px',
                                  borderRight: '1px solid #dee2e6',
                                  backgroundColor: '#f5f5f5',
                                  width: '25%',
                                  padding: '1px 1px',
                                  height: '10px',
                                  position: 'sticky',
                                  top: '27px',
                                  zIndex: 1
                                }}
                              >
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2px', justifyContent: 'center' }}>
                                  <span>P.ban</span>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={isAllDepartmentsCoordSelected}
                                        onChange={handleAllDepartmentsCoordChange}
                                        size="small"
                                        sx={{ 
                                          transform: 'scale(1.1)',
                                          '& .MuiSvgIcon-root': { fontSize: '1.2rem' }
                                        }}
                                      />
                                    }
                                    label=""
                                    sx={{ margin: 0 }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell 
                                align="center" 
                                sx={{ 
                                  fontSize: '10px',
                                  borderRight: '1px solid #dee2e6',
                                  backgroundColor: '#f5f5f5',
                                  width: '25%',
                                  padding: '1px 1px',
                                  height: '10px',
                                  position: 'sticky',
                                  top: '27px',
                                  zIndex: 1
                                }}
                              >
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2px', justifyContent: 'center' }}>
                                  <span>C.Nhân</span>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={isAllCoordinatorsSelected}
                                        onChange={handleAllCoordinatorsChange}
                                        size="small"
                                        sx={{ 
                                          transform: 'scale(1.1)',
                                          '& .MuiSvgIcon-root': { fontSize: '1.2rem' }
                                        }}
                                      />
                                    }
                                    label=""
                                    sx={{ margin: 0 }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell 
                                align="center" 
                                sx={{ 
                                  fontSize: '10px',
                                  borderRight: '1px solid #dee2e6',
                                  backgroundColor: '#f5f5f5',
                                  width: '25%',
                                  padding: '1px 1px',
                                  height: '10px',
                                  position: 'sticky',
                                  top: '27px',
                                  zIndex: 1
                                }}
                              >
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2px', justifyContent: 'center' }}>
                                  <span>P.ban</span>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={isAllDepartmentsSelected}
                                        onChange={handleAllDepartmentsChange}
                                        size="small"
                                        sx={{ 
                                          transform: 'scale(1.1)',
                                          '& .MuiSvgIcon-root': { fontSize: '1.2rem' }
                                        }}
                                      />
                                    }
                                    label=""
                                    sx={{ margin: 0 }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell 
                                align="center" 
                                sx={{ 
                                  fontSize: '10px',
                                  backgroundColor: '#f5f5f5',
                                  width: '25%',
                                  padding: '1px 1px',
                                  height: '10px',
                                  position: 'sticky',
                                  top: '27px',
                                  zIndex: 1
                                }}
                              >
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2px', justifyContent: 'center' }}>
                                  <span>C.Nhân</span>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={isAllUsersSelected}
                                        onChange={handleAllUsersChange}
                                        size="small"
                                        sx={{ 
                                          transform: 'scale(1.1)',
                                          '& .MuiSvgIcon-root': { fontSize: '1.2rem' }
                                        }}
                                      />
                                    }
                                    label=""
                                    sx={{ margin: 0 }}
                                  />
                                </Box>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {assignTreeData.length > 0 ? (
                              assignTreeData.map((node, index) => {
                                // Nếu có search results, chỉ hiển thị departments có kết quả
                                if (searchResults.length > 0) {
                                  const hasMatchingUsers = node.children?.some(child => 
                                    searchResults.includes(child.id)
                                  );
                                  if (!hasMatchingUsers) return null;
                                }
                                
                                // Nếu chỉ hiển thị những người đã chọn
                                if (showOnlySelected) {
                                  const hasSelectedUsers = node.children?.some(child => 
                                    assignData.mainHandler === child.id || 
                                    assignData.coordinators.includes(child.id) || 
                                    assignData.followers.includes(child.id)
                                  );
                                  if (!hasSelectedUsers) return null;
                                }
                                
                                return (
                                  <React.Fragment key={node.id}>
                                    {renderAssignTreeNode(node, index)}
                                    {node.expanded && node.children && node.children.map((child, childIndex) => {
                                      // Nếu có search results, chỉ hiển thị users match
                                      if (searchResults.length > 0 && !searchResults.includes(child.id)) {
                                        return null;
                                      }
                                      
                                      // Nếu chỉ hiển thị những người đã chọn
                                      if (showOnlySelected) {
                                        const isSelected = assignData.mainHandler === child.id || 
                                                         assignData.coordinators.includes(child.id) || 
                                                         assignData.followers.includes(child.id);
                                        if (!isSelected) return null;
                                      }
                                      
                                      return renderAssignTreeNode(child, childIndex);
                                    })}
                                  </React.Fragment>
                                );
                              })
                            ) : (
                              <TableRow>
                                <TableCell colSpan={6} sx={{ 
                                  padding: '2px 2px', 
                                  textAlign: 'center',
                                  fontSize: '10px',
                                  color: '#666'
                                }}>
                                  Không có dữ liệu
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </Box>
              </Box>
              </Box>
            </Grid>

            {/* Bên phải - Files và danh sách người dùng đã chọn */}
              <Grid item xs={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
                {/* Files section - Độ cao bằng "Thông tin văn bản" + "Form chuyển xử lý" */}
                <Box sx={{ 
                  height: '250px', // Ước tính: 200px (thông tin văn bản) + 120px (form chuyển xử lý)
                  overflowY: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1
                }}>
                <DocumentInfoAndFiles 
                  documentData={documentData}
                  showPreview={true}
                  showOnlyFiles={true}
                />
                </Box>
                
                {/* Personal Groups Selection */}
                <Box sx={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 1,
                  backgroundColor: '#f8f9fa'
                }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ color: '#666' }}>Chọn nhóm cá nhân</InputLabel>
                    <Select
                      value={selectedPersonalGroup}
                      onChange={(e) => handlePersonalGroupChange(e.target.value)}
                      label="Chọn nhóm cá nhân"
                      disabled={personalGroupsLoading}
                      sx={{ 
                        fontSize: '13px',
                        color: '#333',
                        backgroundColor: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#ccc',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#999',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#666',
                        }
                      }}
                    >
                      <MenuItem value="">-- Chọn nhóm --</MenuItem>
                      {Array.isArray(personalGroups) && personalGroups.map((group) => (
                        <MenuItem key={group.id} value={group.id}>
                          {group.group_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                
                {/* Danh sách người dùng đã chọn */}
                <Box sx={{ 
                  flex: 1,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 1,
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0
                }}>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 'bold', 
                    color: '#1976d2',
                    mb: 1,
                    pb: 1
                  }}>
                    Danh sách người dùng đã chọn ({selectedCoProcessors.length})
                  </Typography>
                  
                  {selectedCoProcessors.length > 0 ? (
                    <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                      {(() => {
                        // Kiểm tra xem có "Lãnh đạo ban" trong danh sách không
                        const hasLanhDaoBan = selectedCoProcessors.some(itemId => {
                          if (typeof itemId === 'string' && itemId.startsWith('dept_')) {
                            const departmentNodeId = itemId.replace('dept_', '');
                            const departmentNode = assignTreeData.find(n => n.id === departmentNodeId);
                            return departmentNode?.name === 'Lãnh đạo ban';
                          } else {
                            const allUsers = assignTreeData.flatMap(node => 
                              node.children?.filter(child => child.type === 'user') || []
                            );
                            const user = allUsers.find(u => u.user?.id === itemId);
                            if (user) {
                              const userDepartment = assignTreeData.find(node => 
                                node.children?.some(child => child.id === user.id)
                              );
                              return userDepartment?.name === 'Lãnh đạo ban';
                            }
                          }
                          return false;
                        });

                        // Hàm lấy position priority
                        const getPositionPriority = (position: string, departmentName: string): number => {
                          const pos = (position || '').toLowerCase();
                          const dept = (departmentName || '').toLowerCase();
                          
                          // Lãnh đạo ban
                          if (dept.includes('lãnh đạo ban') || pos.includes('trưởng ban') || pos.includes('phó trưởng ban')) {
                            return 1;
                          }
                          // Trưởng phòng
                          if (pos.includes('trưởng phòng') && !pos.includes('phó')) {
                            return 2;
                          }
                          // Phó phòng
                          if (pos.includes('phó trưởng phòng') || pos.includes('phó phòng')) {
                            return 3;
                          }
                          // Nhân viên, Chuyên viên, Văn thư, Admin
                          return 4;
                        };

                        return selectedCoProcessors
                          .map(itemId => {
                            // Xác định vai trò để sắp xếp
                            let rolePriority = 3; // Mặc định: Xem để biết
                            let positionPriority = 999; // Mặc định: Nhân viên
                            
                            if (typeof itemId === 'string' && itemId.startsWith('dept_')) {
                              // Xử lý department
                              const departmentNodeId = itemId.replace('dept_', '');
                              const departmentNode = assignTreeData.find(n => n.id === departmentNodeId);
                              const deptName = departmentNode?.name || '';
                              
                              // Với department, coi như Lãnh đạo ban nếu tên là "Lãnh đạo ban"
                              if (deptName === 'Lãnh đạo ban') {
                                positionPriority = 1;
                              } else {
                                positionPriority = 999; // Các phòng ban khác
                              }
                              
                              const isMainHandler = assignData.mainHandler === departmentNodeId;
                              const isCoordinator = assignData.coordinators.includes(departmentNodeId);
                              const isFollower = assignData.followers.includes(departmentNodeId);
                              
                              if (isMainHandler) rolePriority = 1; // Xử lý chính
                              else if (isCoordinator) rolePriority = 2; // Phối hợp
                              else if (isFollower) rolePriority = 3; // Xem để biết
                            } else {
                              // Xử lý user
                              const allUsers = assignTreeData.flatMap(node => 
                                node.children?.filter(child => child.type === 'user') || []
                              );
                              const user = allUsers.find(u => u.user?.id === itemId);
                              if (user) {
                                const position = user.user?.position || '';
                                
                                // Tìm phòng ban của user
                                const userDepartment = assignTreeData.find(node => 
                                  node.children?.some(child => child.id === user.id)
                                );
                                const deptName = userDepartment?.name || '';
                                
                                // Xác định độ ưu tiên position
                                positionPriority = getPositionPriority(position, deptName);
                                
                                const userNodeId = user.id;
                                const isMainHandler = assignData.mainHandler === userNodeId;
                                const isCoordinator = assignData.coordinators.includes(userNodeId);
                                const isFollower = assignData.followers.includes(userNodeId);
                                
                                if (isMainHandler) rolePriority = 1; // Xử lý chính
                                else if (isCoordinator) rolePriority = 2; // Phối hợp
                                else if (isFollower) rolePriority = 3; // Xem để biết
                              }
                            }
                            
                            return { itemId, rolePriority, positionPriority };
                          })
                          .sort((a, b) => {
                            // 1. Sắp xếp theo vai trò trước: Xử lý chính > Phối hợp > Xem để biết
                            if (a.rolePriority !== b.rolePriority) {
                              return a.rolePriority - b.rolePriority;
                            }
                            
                            // 2. Trong cùng vai trò, sắp xếp theo position: Lãnh đạo ban > Trưởng phòng > Phó phòng > Nhân viên
                            return a.positionPriority - b.positionPriority;
                          })
                        .map(({ itemId }) => {
                        // Kiểm tra xem là user hay department
                        if (typeof itemId === 'string' && itemId.startsWith('dept_')) {
                          // Xử lý department
                          const departmentNodeId = itemId.replace('dept_', '');
                          const departmentNode = assignTreeData.find(n => n.id === departmentNodeId);
                          
                          if (!departmentNode) {
                            return <React.Fragment key={itemId} />;
                          }
                          
                          // Xác định vai trò của department
                          const isMainHandler = assignData.mainHandler === departmentNodeId;
                          const isCoordinator = assignData.coordinators.includes(departmentNodeId);
                          const isFollower = assignData.followers.includes(departmentNodeId);
                          
                          // Nếu không có vai trò nào thì không hiển thị
                          if (!isMainHandler && !isCoordinator && !isFollower) {
                            return <React.Fragment key={itemId} />;
                          }
                          
                          // Xác định vai trò hiện tại
                          let currentRole: 'main' | 'coord' | 'follow' = 'follow';
                          if (isMainHandler) {
                            currentRole = 'main';
                          } else if (isCoordinator) {
                            currentRole = 'coord';
                          } else if (isFollower) {
                            currentRole = 'follow';
                          }
                          
                          return (
                            <Box 
                              key={itemId}
                              onMouseEnter={() => setHoveredUserId(null)} // Không highlight cho department
                              onMouseLeave={() => setHoveredUserId(null)}
                              sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                p: 1,
                                mb: 1,
                                backgroundColor: 'white',
                                borderRadius: 1,
                                border: '1px solid #e0e0e0',
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: '#f5f5f5',
                                  borderColor: '#1976d2'
                                }
                              }}
                            >
                              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {departmentNode.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#666' }}>
                                  Phòng ban
                                </Typography>
                                <FormControl size="small" sx={{ mt: 0.5, width: '100%' }}>
                                  <Select
                                    value={currentRole}
                                    onChange={(e) => handleRoleChange(itemId, e.target.value as 'main' | 'coord' | 'follow')}
                                    sx={{
                                      fontSize: '0.75rem',
                                      height: '30px',
                                      color: currentRole === 'main' ? '#1976d2' : currentRole === 'coord' ? '#ed6c02' : '#2e7d32',
                                      '& .MuiSelect-select': {
                                        py: 0.5,
                                        px: 1,
                                        height: '30px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: currentRole === 'main' ? '#1976d2' : currentRole === 'coord' ? '#ed6c02' : '#2e7d32',
                                      },
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: currentRole === 'main' ? '#1976d2' : currentRole === 'coord' ? '#ed6c02' : '#2e7d32',
                                      },
                                      '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: currentRole === 'main' ? '#1565c0' : currentRole === 'coord' ? '#e65100' : '#1b5e20',
                                      },
                                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: currentRole === 'main' ? '#1976d2' : currentRole === 'coord' ? '#ed6c02' : '#2e7d32',
                                      },
                                      '& .MuiSvgIcon-root': {
                                        color: currentRole === 'main' ? '#1976d2' : currentRole === 'coord' ? '#ed6c02' : '#2e7d32',
                                      }
                                    }}
                                  >
                                    <MenuItem value="main" sx={{ fontSize: '0.75rem', color: '#1976d2' }}>
                                      Xử lý chính
                                    </MenuItem>
                                    <MenuItem value="coord" sx={{ fontSize: '0.75rem', color: '#ed6c02' }}>
                                      Phối hợp
                                    </MenuItem>
                                    <MenuItem value="follow" sx={{ fontSize: '0.75rem', color: '#2e7d32' }}>
                                      Xem để biết
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveUser(itemId)}
                                sx={{
                                  color: '#d32f2f',
                                  '&:hover': {
                                    backgroundColor: 'rgba(211, 47, 47, 0.08)'
                                  }
                                }}
                              >
                                <CloseIcon />
                              </IconButton>
                            </Box>
                          );
                        } else {
                          // Xử lý user (logic cũ)
                          const allUsers = assignTreeData.flatMap(node => 
                            node.children?.filter(child => child.type === 'user') || []
                          );
                          const user = allUsers.find(u => u.user?.id === itemId);
                          
                          if (!user) {
                            return <React.Fragment key={itemId} />;
                          }
                        
                        // Xác định vai trò của user
                        const userNodeId = user.id;
                        const isMainHandler = assignData.mainHandler === userNodeId;
                        const isCoordinator = assignData.coordinators.includes(userNodeId);
                        const isFollower = assignData.followers.includes(userNodeId);
                        
                        // Nếu không có vai trò nào thì không hiển thị
                        if (!isMainHandler && !isCoordinator && !isFollower) {
                          return <React.Fragment key={itemId} />;
                        }
                        
                        // Xác định vai trò hiện tại
                        let currentRole: 'main' | 'coord' | 'follow' = 'follow';
                        if (isMainHandler) {
                          currentRole = 'main';
                        } else if (isCoordinator) {
                          currentRole = 'coord';
                        } else if (isFollower) {
                          currentRole = 'follow';
                        }
                        
                          return (
                            <Box 
                              key={itemId}
                              onMouseEnter={() => setHoveredUserId(itemId)}
                              onMouseLeave={() => setHoveredUserId(null)}
                              sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                p: 1,
                                mb: 1,
                                backgroundColor: 'white',
                                borderRadius: 1,
                                border: '1px solid #e0e0e0',
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: '#f5f5f5',
                                  borderColor: '#1976d2'
                                }
                              }}
                            >
                              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {user.user?.fullname}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#666' }}>
                                  {user.user?.username} • {user.user?.position_label || user.user?.position || 'Nhân viên'}
                                </Typography>
                                <FormControl sx={{ m: 0, p: 0, width: '50%', minHeight: '30px', height: '30px' }}>
                                  <Select
                                    value={currentRole}
                                    onChange={(e) => handleRoleChange(itemId, e.target.value as 'main' | 'coord' | 'follow')}
                                    sx={{
                                      fontSize: '0.75rem',
                                      minHeight: '30px',
                                      height: '30px',
                                      m: 0,
                                      p: 0,
                                      color: currentRole === 'main' ? '#1976d2' : currentRole === 'coord' ? '#ed6c02' : '#2e7d32',
                                      '& .MuiSelect-select': {
                                        m: 0,
                                        p: 0,
                                        py: 0.5,
                                        px: 1,
                                        height: '30px',
                                        minHeight: '30px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: currentRole === 'main' ? '#1976d2' : currentRole === 'coord' ? '#ed6c02' : '#2e7d32',
                                      },
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: currentRole === 'main' ? '#1976d2' : currentRole === 'coord' ? '#ed6c02' : '#2e7d32',
                                      },
                                      '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: currentRole === 'main' ? '#1565c0' : currentRole === 'coord' ? '#e65100' : '#1b5e20',
                                      },
                                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: currentRole === 'main' ? '#1976d2' : currentRole === 'coord' ? '#ed6c02' : '#2e7d32',
                                      },
                                      '& .MuiSvgIcon-root': {
                                        color: currentRole === 'main' ? '#1976d2' : currentRole === 'coord' ? '#ed6c02' : '#2e7d32',
                                      }
                                    }}
                                  >
                                    <MenuItem value="main" sx={{ fontSize: '0.75rem', color: '#1976d2' }}>
                                      Xử lý chính
                                    </MenuItem>
                                    <MenuItem value="coord" sx={{ fontSize: '0.75rem', color: '#ed6c02' }}>
                                      Phối hợp
                                    </MenuItem>
                                    <MenuItem value="follow" sx={{ fontSize: '0.75rem', color: '#2e7d32' }}>
                                      Xem để biết
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveUser(itemId)}
                                sx={{
                                  color: '#d32f2f',
                                  '&:hover': {
                                    backgroundColor: 'rgba(211, 47, 47, 0.08)'
                                  }
                                }}
                              >
                                <CloseIcon />
                              </IconButton>
                            </Box>
                          );
                        }
                      });
                      })()}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ 
                      color: '#666', 
                      fontStyle: 'italic',
                      textAlign: 'center',
                      py: 2
                    }}>
                      Chưa có người dùng nào được chọn
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
      </DialogContent>

      <DialogActions sx={{ 
        p: 0.5, // Giảm padding xuống 4px
        borderTop: 1, 
        borderColor: 'divider',
        backgroundColor: '#e0e0e0', // Màu ghi đậm hơn
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1
      }}>
        {/* Quick Selection Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel sx={{ color: '#666' }}>Chọn nhanh danh sách</InputLabel>
            <Select
              value={quickSelection}
              onChange={(e) => handleQuickSelectionChange(e.target.value)}
              label="Chọn nhanh danh sách"
              sx={{ 
                fontSize: '13px',
                color: '#333',
                backgroundColor: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ccc',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#999',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#666',
                }
              }}
            >
              <MenuItem value="">-- Chọn danh sách --</MenuItem>
              {currentUserInfo?.canViewAllUsers && (
                <>
                  <MenuItem value="lanh_dao_ban">Lãnh đạo ban</MenuItem>
                  <MenuItem value="lanh_dao_ban_phong">Lãnh đạo ban và các phòng</MenuItem>
                  <MenuItem value="truong_phong">Trưởng phòng</MenuItem>
                </>
              )}
              <MenuItem value="cung_phong">Cùng phòng</MenuItem>
              <MenuItem value="nhom_ca_nhan">Nhóm cá nhân</MenuItem>
            </Select>
          </FormControl>

        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={submitting}
            sx={{
              fontSize: '13px',
              borderColor: '#ccc',
              color: '#666',
              backgroundColor: 'white',
              '&:hover': {
                borderColor: '#999',
                backgroundColor: '#f0f0f0'
              }
            }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            sx={{
              fontSize: '13px',
              backgroundColor: '#086428',
              '&:hover': {
                backgroundColor: '#064e1a'
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#999'
              }
            }}
          >
            {submitting ? 'Đang xử lý...' : 'Chuyển xử lý'}
          </Button>
        </Box>
      </DialogActions>

      {/* Dialog chọn vai trò cho phòng ban */}
      <Dialog
        open={departmentRoleDialogOpen}
        onClose={() => setDepartmentRoleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: 'primary.main', 
          color: 'white',
          height: 51,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxSizing: 'border-box'
        }}>
          <Typography component="span" variant="h6">
            {selectedDepartmentId.startsWith('personal_group_') 
              ? 'Chọn vai trò cho nhóm cá nhân' 
              : 'Chọn vai trò cho phòng ban'}
          </Typography>
          <IconButton
            onClick={() => setDepartmentRoleDialogOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Bạn muốn chọn <strong>"{selectedDepartmentName}"</strong> cho vai trò nào?
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (selectedDepartmentId.startsWith('personal_group_')) {
                  handlePersonalGroupRoleSelect('coord');
                } else {
                  handleDepartmentRoleSelect('coord');
                }
              }}
              sx={{ flex: 1 }}
            >
              Phối hợp xử lý
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                if (selectedDepartmentId.startsWith('personal_group_')) {
                  handlePersonalGroupRoleSelect('follow');
                } else {
                  handleDepartmentRoleSelect('follow');
                }
              }}
              sx={{ flex: 1 }}
            >
              Xem để biết
            </Button>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDepartmentRoleDialogOpen(false)}
            variant="outlined"
          >
            Hủy
          </Button>
        </DialogActions>
      </Dialog>

      {/* YKienMau Popup */}
      <YKienMauPopup
        open={yKienMauOpen}
        onClose={handleCloseYKienMau}
        onTemplateSelect={handleTemplateSelect}
      />
    </Dialog>
  );
};

export default ChuyenXuLyPopup;
